/**
 * Omni Virtual Counter - konfigurator frontend.
 *
 * Vanilla JS (bez zależności), obsługuje: pobieranie danych przez REST API,
 * drag & drop (desktop + touch) z fallbackiem na przyciski, limity,
 * sesję WooCommerce, edycję zestawu i dodawanie do koszyka.
 *
 * WAŻNE: żadna cena/dostępność wyświetlana tu nie jest traktowana jako
 * ostateczna - backend (REST /validate, /cart/add) zawsze przelicza
 * wszystko od nowa przed dodaniem do koszyka.
 */
( function () {
	'use strict';

	if ( typeof window.omniVcConfig === 'undefined' ) {
		return;
	}

	var CFG = window.omniVcConfig;
	var HIDDEN_CATEGORY_SLUGS_BY_COUNTER = {
		'lada-cukiernicza': [ 'torty-i-ciasta', 'pieczywo', 'torty' ],
	};
	var COUNTERS_WITHOUT_CATEGORY_FILTERS = [ 'lada-tortow', 'lada-pieczywa' ];

	/**
	 * Eleganckie zastępniki graficzne (inline SVG, bez zależności od
	 * zewnętrznych plików) pokazywane, gdy prawdziwa miniatura produktu
	 * nie może się załadować (błędny/brakujący URL) - motyw "pudełka
	 * prezentowego" pasuje tematycznie do konfiguratora zestawów.
	 */
	var PRODUCT_IMG_FALLBACK =
		'data:image/svg+xml;utf8,' +
		encodeURIComponent(
			'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">' +
				'<rect width="100" height="100" fill="#f3ece3"/>' +
				'<g fill="none" stroke="#c8956c" stroke-width="4" stroke-linecap="round" stroke-linejoin="round">' +
				'<rect x="22" y="42" width="56" height="38" rx="4"/>' +
				'<path d="M22 54h56"/>' +
				'<path d="M50 42v38"/>' +
				'<path d="M38 42c-6-10 2-18 12-10 10-8 18 0 12 10"/>' +
				'</g>' +
				'</svg>'
		);

	var SLOT_IMG_FALLBACK =
		'data:image/svg+xml;utf8,' +
		encodeURIComponent(
			'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">' +
				'<rect width="100" height="100" fill="#241209"/>' +
				'<g fill="none" stroke="#c8956c" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" opacity="0.75">' +
				'<rect x="22" y="42" width="56" height="38" rx="4"/>' +
				'<path d="M22 54h56"/>' +
				'<path d="M50 42v38"/>' +
				'<path d="M38 42c-6-10 2-18 12-10 10-8 18 0 12 10"/>' +
				'</g>' +
				'</svg>'
		);

	/**
	 * Podpina fallback: jeśli obraz nie załaduje się (błędny/martwy URL),
	 * pokazuje elegancką grafikę zastępczą zamiast domyślnej "połamanej"
	 * ikony przeglądarki.
	 *
	 * @param {HTMLImageElement} img
	 * @param {string} fallback
	 */
	function withImageFallback( img, fallback ) {
		img.addEventListener(
			'error',
			function () {
				if ( fallback === SLOT_IMG_FALLBACK ) {
					img.hidden = true;
					img.classList.add( 'omni-vc-img-fallback' );
					if ( img.parentElement ) {
						img.parentElement.classList.add( 'is-missing-image' );
					}
					return;
				}

				if ( img.src !== fallback ) {
					img.onerror = null;
					img.src = fallback;
					img.classList.add( 'omni-vc-img-fallback' );
				}
			},
			{ once: true }
		);
	}

	// --------------------------------------------------------------
	// Narzędzia REST.
	// --------------------------------------------------------------

	/**
	 * Łączy bazowy adres REST z endpointem, gwarantując dokładnie jeden "/"
	 * między nimi (niezależnie od tego, czy restUrl ma końcowy slash).
	 *
	 * @param {string} path
	 * @return {string}
	 */
	function restEndpoint( path ) {
		var base = CFG.restUrl.replace( /\/+$/, '' );
		var rel = String( path ).replace( /^\/+/, '' );
		return base + '/' + rel;
	}

	/**
	 * @param {string} path
	 * @param {Object} [params]
	 * @return {Promise<any>}
	 */
	function apiGet( path, params ) {
		var url = restEndpoint( path );
		if ( params ) {
			var query = Object.keys( params )
				.filter( function ( k ) {
					return params[ k ] !== undefined && params[ k ] !== null && params[ k ] !== '';
				} )
				.map( function ( k ) {
					return encodeURIComponent( k ) + '=' + encodeURIComponent( params[ k ] );
				} )
				.join( '&' );
			if ( query ) {
				url += '?' + query;
			}
		}
		return fetch( url, {
			headers: { 'X-WP-Nonce': CFG.nonce },
			credentials: 'same-origin',
		} ).then( function ( res ) {
			return res.json().then(
				function ( data ) {
					return { ok: res.ok, status: res.status, data: data };
				},
				function ( parseErr ) {
					throw new Error( 'Nieprawidłowa odpowiedź serwera (' + url + '): ' + parseErr.message );
				}
			);
		} );
	}

	/**
	 * @param {string} path
	 * @param {Object} body
	 * @param {string} [method]
	 * @return {Promise<any>}
	 */
	function apiSend( path, body, method ) {
		return fetch( restEndpoint( path ), {
			method: method || 'POST',
			credentials: 'same-origin',
			cache: 'no-store',
			headers: {
				'Content-Type': 'application/json',
				'X-WP-Nonce': CFG.nonce,
				'X-Omni-VC-Token': CFG.guestToken || '',
			},
			body: JSON.stringify( body || {} ),
		} ).then( function ( res ) {
			return res.text().then( function ( text ) {
				var data = {};
				if ( text ) {
					try {
						data = JSON.parse( text );
					} catch ( parseErr ) {
						throw new Error( 'Nieprawidłowa odpowiedź serwera (' + path + ', HTTP ' + res.status + ').' );
					}
				}
				return { ok: res.ok, status: res.status, data: data };
			} );
		} );
	}

	/**
	 * Bezpiecznie normalizuje wartość do tablicy. Chroni przed sytuacją,
	 * gdy REST (przez cache/proxy/hosting) zwróci pustą tablicę PHP jako
	 * obiekt JSON "{}" zamiast "[]", albo gdy pole w ogóle nie przyjdzie.
	 *
	 * @param {*} value
	 * @return {Array}
	 */
	function toArray( value ) {
		if ( Array.isArray( value ) ) {
			return value;
		}
		if ( value && 'object' === typeof value ) {
			return Object.keys( value ).map( function ( k ) {
				return value[ k ];
			} );
		}
		return [];
	}

	function debounce( fn, wait ) {
		var t;
		return function () {
			var args = arguments,
				ctx = this;
			clearTimeout( t );
			t = setTimeout( function () {
				fn.apply( ctx, args );
			}, wait );
		};
	}

	function formatPrice( amount, currency ) {
		try {
			return new Intl.NumberFormat( document.documentElement.lang || 'pl-PL', {
				style: 'currency',
				currency: currency || 'PLN',
			} ).format( amount );
		} catch ( e ) {
			return Number( amount ).toFixed( 2 ) + ' ' + ( currency || '' );
		}
	}

	/**
	 * @param {Array} left
	 * @param {Array} right
	 * @return {boolean}
	 */
	function hasCategoryIntersection( left, right ) {
		var rightIds = toArray( right ).map( function ( id ) {
			return parseInt( id, 10 ) || 0;
		} );

		return toArray( left ).some( function ( id ) {
			return -1 !== rightIds.indexOf( parseInt( id, 10 ) || 0 );
		} );
	}

	/**
	 * @param {string} value
	 * @return {string}
	 */
	function normalizeCategoryKey( value ) {
		return String( value || '' )
			.toLowerCase()
			.normalize( 'NFD' )
			.replace( /[\u0300-\u036f]/g, '' )
			.replace( /[^a-z0-9]+/g, '-' )
			.replace( /^-+|-+$/g, '' );
	}

	// --------------------------------------------------------------
	// Klasa główna konfiguratora (jedna instancja na wystąpienie shortcode).
	// --------------------------------------------------------------

	function OmniVirtualCounter( root ) {
		this.root = root;
		this.currency = 'PLN';
		this.decimals = 2;
		this.categories = [];
		this.categoriesListenerBound = false;
		this.categorySelectListenerBound = false;
		this.counters = [];
		this.currentCounter = null;
		this.packages = [];
		this.currentPackage = null;
		this.slots = []; // Array(length=slots_total) of null | {product_id, variation_id, name, price, thumbnail}
		this.configurationId = null;
		this.editingCartItemKey = null;
		this.currentCategory = 0;
		this.searchTerm = '';
		this.page = 1;
		this.pages = 1;
		this.productsPerPage = 8;
		this.pickupItems = [];
		this.pickupDeliveryMethod = 'pickup';
		this.inquiryProduct = null;
		this.previousPage = 1;
		this.packagePage = 1;
		this.packagePageSize = 3;
		this.evaluation = null;
		this.dragData = null;
		this.productsRequestSeq = 0;
		this.validationRequestSeq = 0;
		this.activeNotices = {}; // klucz "type:message" -> { node, timer } (dedup + odświeżanie czasu).
		this.evaluationNoticeKeys = []; // klucze komunikatów z ostatniej walidacji (do czyszczenia nieaktualnych).

		this.els = {
			heading: root.querySelector( '.omni-vc-heading' ),
			counterTabs: root.querySelector( '#omni-vc-counter-tabs' ),
			counterFilter: root.querySelector( '#omni-vc-counter-filter' ),
			counterSelect: root.querySelector( '#omni-vc-counter-select' ),
			categories: root.querySelector( '#omni-vc-categories' ),
			categoryFilter: root.querySelector( '#omni-vc-category-filter' ),
			categorySelect: root.querySelector( '#omni-vc-category-select' ),
			search: root.querySelector( '#omni-vc-search-input' ),
			productsGrid: root.querySelector( '#omni-vc-products-grid' ),
			pagination: root.querySelector( '#omni-vc-pagination' ),
			inquiryDetailBar: root.querySelector( '#omni-vc-inquiry-detail-bar' ),
			inquiryBack: root.querySelector( '#omni-vc-inquiry-back' ),
			packageSelect: root.querySelector( '#omni-vc-package-select' ),
			packagePicker: root.querySelector( '#omni-vc-package-picker' ),
			packageCards: root.querySelector( '#omni-vc-package-cards' ),
			packagePager: root.querySelector( '#omni-vc-package-pager' ),
			packagePrev: root.querySelector( '#omni-vc-package-prev' ),
			packageNext: root.querySelector( '#omni-vc-package-next' ),
			packagePageStatus: root.querySelector( '#omni-vc-package-page-status' ),
			slotsGrid: root.querySelector( '#omni-vc-slots-grid' ),
			packageVisual: root.querySelector( '.omni-vc-package-visual' ),
			notices: root.querySelector( '#omni-vc-notices' ),
			resumeBar: root.querySelector( '#omni-vc-resume-bar' ),
			resumeContinue: root.querySelector( '#omni-vc-resume-continue' ),
			resumeClear: root.querySelector( '#omni-vc-resume-clear' ),
			summaryPackageName: root.querySelector( '#omni-vc-summary-package-name' ),
			summarySlots: root.querySelector( '#omni-vc-summary-slots' ),
			slotsBar: root.querySelector( '#omni-vc-slots-bar' ),
			summaryWeight: root.querySelector( '#omni-vc-summary-weight' ),
			weightBar: root.querySelector( '#omni-vc-weight-bar' ),
			summaryItems: root.querySelector( '#omni-vc-summary-items' ),
			summaryProductsValue: root.querySelector( '#omni-vc-summary-products-value' ),
			summaryPackagePrice: root.querySelector( '#omni-vc-summary-package-price' ),
			summaryTotal: root.querySelector( '#omni-vc-summary-total' ),
			addToCart: root.querySelector( '#omni-vc-add-to-cart' ),
			clearAll: root.querySelector( '#omni-vc-clear-all' ),
			orderDateWrap: root.querySelector( '#omni-vc-order-date-wrap' ),
			orderDate: root.querySelector( '#omni-vc-order-date' ),
			deliveryMethod: root.querySelector( '#omni-vc-delivery-method' ),
			emptyHint: root.querySelector( '#omni-vc-empty-hint' ),
			summary: root.querySelector( '#omni-vc-summary' ),
			inquiryForm: root.querySelector( '#omni-vc-inquiry-form' ),
			inquiryName: root.querySelector( '#omni-vc-inquiry-name' ),
			inquiryPhone: root.querySelector( '#omni-vc-inquiry-phone' ),
			inquiryEmail: root.querySelector( '#omni-vc-inquiry-email' ),
			inquiryQty: root.querySelector( '#omni-vc-inquiry-qty' ),
			inquiryDate: root.querySelector( '#omni-vc-inquiry-date' ),
			inquiryLocation: root.querySelector( '#omni-vc-inquiry-location' ),
			inquiryInvoice: root.querySelector( '#omni-vc-inquiry-invoice' ),
			inquiryNipWrap: root.querySelector( '#omni-vc-inquiry-nip-wrap' ),
			inquiryNip: root.querySelector( '#omni-vc-inquiry-nip' ),
			inquiryMessage: root.querySelector( '#omni-vc-inquiry-message' ),
			inquirySubmit: root.querySelector( '#omni-vc-inquiry-submit' ),
			tmplProduct: document.getElementById( 'omni-vc-tmpl-product-card' ),
			tmplSlot: document.getElementById( 'omni-vc-tmpl-slot' ),
			tmplNotice: document.getElementById( 'omni-vc-tmpl-notice' ),
		};

		this.pendingSlots = null; // trzymane tymczasowo przy zmianie opakowania (potwierdzenie).

		this.init();
	}

	OmniVirtualCounter.prototype.init = function () {
		var self = this;

		this.bindStaticEvents();

		Promise.all( [ apiGet( 'settings' ), apiGet( 'categories' ), apiGet( 'packages' ), apiGet( 'counters' ) ] )
			.then( function ( results ) {
				var settingsRes = results[ 0 ];
				var categoriesRes = results[ 1 ];
				var packagesRes = results[ 2 ];
				var countersRes = results[ 3 ];

				if ( ! settingsRes.ok || ! categoriesRes.ok || ! packagesRes.ok || ! countersRes.ok ) {
					throw new Error(
						'Bootstrap REST error - settings:' + settingsRes.status +
							' categories:' + categoriesRes.status +
							' packages:' + packagesRes.status +
							' counters:' + countersRes.status
					);
				}

				self.currency = settingsRes.data.currency || 'PLN';
				self.decimals = settingsRes.data.decimals || 2;
				CFG.guestToken = settingsRes.data.guest_token || CFG.guestToken || '';
				CFG.settings = Object.assign( {}, CFG.settings || {}, {
					autoPackage: !! settingsRes.data.auto_package,
					hidePackage: !! settingsRes.data.hide_package,
					multiplePackages: !! settingsRes.data.multiple_packages,
					packageStrategy: settingsRes.data.package_strategy || 'smallest',
				} );
				self.categories = toArray( categoriesRes.data );
				self.packages = toArray( packagesRes.data );
				self.counters = toArray( countersRes.data );
				self.currentCounter = self.getPublicCounters()[ 0 ] || self.counters[ 0 ] || null;
				self.updateCounterModeUI();

				self.renderCounterTabs();
				self.renderCategories();
				self.renderPackageOptions();

				if ( ! self.packages.length && self.isPackageCounter() ) {
					self.pushNotice(
						'warning',
						'Brak dostępnych opakowań. Dodaj przynajmniej jedno aktywne opakowanie w WooCommerce > Wirtualna Lada > Opakowania.'
					);
					return;
				}

				self.resolveInitialState();
			} )
			.catch( function ( err ) {
				self.reportFatalError( err );
			} );
	};

	/**
	 * Centralna obsługa błędów sieci/REST - loguje do konsoli i pokazuje
	 * czytelny komunikat zamiast cichego, niewidocznego dla klienta błędu.
	 */
	OmniVirtualCounter.prototype.reportFatalError = function ( err ) {
		// eslint-disable-next-line no-console
		console.error( '[Omni Virtual Counter]', err );
		this.pushNotice(
			'error',
			'Nie udało się załadować konfiguratora. Odśwież stronę; jeśli problem się powtarza, skontaktuj się ze sklepem.'
		);
	};

	// ------------------------------------------------------------------
	// Stan początkowy: edycja z koszyka / wznowienie sesji / świeży start.
	// ------------------------------------------------------------------

	OmniVirtualCounter.prototype.resolveInitialState = function () {
		var self = this;

		if ( CFG.editKey ) {
			apiGet( 'cart/item/' + encodeURIComponent( CFG.editKey ) )
				.then( function ( res ) {
					if ( res.ok && res.data.found ) {
						self.editingCartItemKey = CFG.editKey;
						self.applyConfiguration( res.data.configuration, res.data.evaluation, true );
					} else {
						self.startFresh();
					}
				} )
				.catch( function ( err ) {
					self.reportFatalError( err );
					self.startFresh();
				} );
			return;
		}

		this.pendingResume = null;
		if ( this.els.resumeBar ) {
			this.els.resumeBar.hidden = true;
		}
		apiSend( 'session', {}, 'DELETE' ).catch( function () {
			return null;
		} );
		this.startFresh();
	};

	OmniVirtualCounter.prototype.startFresh = function () {
		var defaultSlug = this.root.dataset.defaultPackage;
		var pkg = null;

		if ( ! this.isPackageCounter() ) {
			this.currentPackage = null;
			this.slots = [];
			this.evaluation = null;
			this.pickupItems = [];
			this.inquiryProduct = null;
			this.updateCounterModeUI();
			this.renderSlots();
			this.renderSummary();
			this.loadProducts();
			return;
		}

		if ( defaultSlug ) {
			pkg = this.packages.filter( function ( p ) {
				return p.slug === defaultSlug;
			} )[ 0 ];
		}

		pkg = pkg || this.packages[ 0 ];

		if ( ! pkg ) {
			this.pushNotice( 'error', 'Brak dostępnych opakowań.' );
			return;
		}

		this.setPackage( pkg, true );
		this.loadProducts();
	};

	OmniVirtualCounter.prototype.canResumeConfiguration = function ( data ) {
		return !! (
			data &&
			data.has_configuration &&
			data.configuration &&
			toArray( data.configuration.slots ).length &&
			data.evaluation &&
			data.evaluation.package &&
			data.evaluation.slots_used > 0
		);
	};

	OmniVirtualCounter.prototype.applyConfiguration = function ( configuration, evaluation, skipFetchProducts ) {
		if ( ! configuration || ! toArray( configuration.slots ).length ) {
			this.pendingResume = null;
			this.els.resumeBar.hidden = true;
			this.startFresh();
			return;
		}

		var pkg = this.packages.filter( function ( p ) {
			return p.id === configuration.package_id;
		} )[ 0 ];

		if ( ! pkg ) {
			this.startFresh();
			return;
		}

		this.configurationId = configuration.configuration_id;
		this.setPackage( pkg, false );

		this.slots = new Array( pkg.slots ).fill( null );
		toArray( configuration.slots ).forEach( function ( slot ) {
			if ( slot.slot < pkg.slots ) {
				this.slots[ slot.slot ] = { product_id: slot.product_id, variation_id: slot.variation_id || 0 };
			}
		}, this );

		this.hydrateSlotsFromEvaluation( evaluation );
		this.evaluation = evaluation;
		this.renderSlots();
		this.renderSummary();
		this.loadProducts();
	};

	/**
	 * Uzupełnia dane produktowe (nazwa/cena/miniatura/rozmiar bloku) slotów na
	 * podstawie ostatniej wyceny serwera (evaluation.items) - to jest jedyne
	 * autorytatywne źródło tego, co faktycznie "trzyma się" w zestawie.
	 *
	 * evaluation.items[].slots to płaska lista zajętych kratek dla danej linii
	 * (produkt może występować w kilku oddzielnych blokach - np. dwa te same
	 * 3-slotowe ciasta w różnych miejscach tacy trafiają do JEDNEJ linii).
	 * Sortujemy te indeksy i dzielimy na kawałki po slots_required, żeby
	 * odtworzyć poszczególne bloki i ich kotwice (patrz sekcja "Bloki
	 * wielosolotowe" wyżej).
	 */
	OmniVirtualCounter.prototype.hydrateSlotsFromEvaluation = function ( evaluation ) {
		if ( ! evaluation || ! evaluation.items ) {
			return;
		}

		var total = this.slots.length;
		var next = new Array( total ).fill( null );
		var covered = new Array( total ).fill( false );

		toArray( evaluation.items ).forEach( function ( item ) {
			var span = Math.max( 1, parseInt( item.slots_required, 10 ) || 1 );
			var indices = toArray( item.slots )
				.map( function ( n ) {
					return parseInt( n, 10 );
				} )
				.sort( function ( a, b ) {
					return a - b;
				} );

			for ( var c = 0; c < indices.length; c += span ) {
				var chunk = indices.slice( c, c + span );
				if ( chunk.length !== span ) {
					continue; // resztki spoza pełnego bloku - dane niespójne, pomiń.
				}
				var anchor = chunk[ 0 ];
				chunk.forEach( function ( idx ) {
					if ( idx >= 0 && idx < total ) {
						next[ idx ] = {
							product_id: item.product_id,
							variation_id: item.variation_id,
							name: item.name,
							price: item.price,
							thumbnail: item.thumbnail,
							slots_required: span,
							anchorIndex: anchor,
							invalid: false,
						};
						covered[ idx ] = true;
					}
				} );
			}
		} );

		// Kratki, które klient miał lokalnie zajęte, ale serwer w ogóle ich nie
		// potwierdził (np. produkt właśnie zniknął ze stanu magazynowego) -
		// zachowaj jako widoczne-ale-nieważne zamiast po cichu usuwać, żeby
		// klient widział co odpadło i mógł to świadomie skasować.
		for ( var i = 0; i < total; i++ ) {
			if ( ! covered[ i ] && this.slots[ i ] ) {
				next[ i ] = Object.assign( {}, this.slots[ i ], { invalid: true } );
			}
		}

		this.slots = next;
	};

	// ------------------------------------------------------------------
	// Renderowanie: kategorie, opakowania, produkty, sloty, podsumowanie.
	// ------------------------------------------------------------------

	OmniVirtualCounter.prototype.getPublicCounters = function () {
		return this.counters;
	};

	OmniVirtualCounter.prototype.getDetailTemplateCounter = function () {
		var product = this.currentProducts && this.currentProducts.length ? this.currentProducts[ 0 ] : null;
		var baseLayout = product ? this.getBaseProductCounterLayout( product ) : null;
		var detailLayout = baseLayout && baseLayout.detail ? baseLayout.detail : null;

		if ( detailLayout && detailLayout.background_image_url ) {
			return {
				image_url: detailLayout.background_image_url,
				image_width: detailLayout.background_image_width || this.currentCounter.image_width,
				image_height: detailLayout.background_image_height || this.currentCounter.image_height,
			};
		}

		return this.currentCounter;
	};

	OmniVirtualCounter.prototype.renderCounterTabs = function () {
		var self = this;
		var publicCounters = this.getPublicCounters();

		if ( ! this.els.counterTabs ) {
			return;
		}

		this.els.counterTabs.innerHTML = '';
		if ( this.els.counterSelect ) {
			this.els.counterSelect.innerHTML = '';
		}
		if ( this.els.counterFilter ) {
			this.els.counterFilter.hidden = publicCounters.length <= 1;
		}

		if ( publicCounters.length <= 1 ) {
			this.els.counterTabs.hidden = true;
			this.applyCounterBackground();
			return;
		}

		this.els.counterTabs.hidden = false;

		publicCounters.forEach( function ( counter ) {
			var btn = document.createElement( 'button' );
			btn.type = 'button';
			btn.className = 'omni-vc-counter-pill' + ( self.currentCounter && counter.slug === self.currentCounter.slug ? ' is-active' : '' );
			btn.dataset.counter = counter.slug;
			btn.setAttribute( 'role', 'tab' );
			btn.setAttribute( 'aria-selected', self.currentCounter && counter.slug === self.currentCounter.slug ? 'true' : 'false' );
			btn.textContent = counter.name;
			self.els.counterTabs.appendChild( btn );
			if ( self.els.counterSelect ) {
				var option = document.createElement( 'option' );
				option.value = counter.slug;
				option.textContent = counter.name;
				self.els.counterSelect.appendChild( option );
			}
		} );
		if ( this.els.counterSelect && this.currentCounter ) {
			this.els.counterSelect.value = this.currentCounter.slug;
			this.els.counterSelect.addEventListener( 'change', function () {
				var selected = Array.prototype.find.call( self.els.counterTabs.querySelectorAll( '.omni-vc-counter-pill' ), function ( btn ) {
					return btn.dataset.counter === self.els.counterSelect.value;
				} );
				if ( selected ) {
					selected.click();
				} else {
					self.els.counterSelect.value = self.currentCounter.slug;
				}
			} );
		}

		this.els.counterTabs.addEventListener( 'click', function ( event ) {
			var btn = event.target.closest( '.omni-vc-counter-pill' );
			if ( ! btn ) {
				return;
			}
			var previousCounter = self.currentCounter;
			self.currentCounter = self.counters.filter( function ( counter ) {
				return counter.slug === btn.dataset.counter;
			} )[ 0 ] || self.currentCounter;

			if ( ! previousCounter || previousCounter.slug === self.currentCounter.slug ) {
				return;
			}

			self.page = 1;
			self.previousPage = 1;
			self.pickupItems = [];
			self.inquiryProduct = null;
			self.els.counterTabs.querySelectorAll( '.omni-vc-counter-pill' ).forEach( function ( item ) {
				var active = item.dataset.counter === self.currentCounter.slug;
				item.classList.toggle( 'is-active', active );
				item.setAttribute( 'aria-selected', active ? 'true' : 'false' );
			} );
			if ( self.els.counterSelect ) {
				self.els.counterSelect.value = self.currentCounter.slug;
			}
			self.updateCounterModeUI();
			self.renderCategories();

			// Lada package, na którą klient trafia po raz pierwszy w tej
			// sesji (np. startowa lada była inquiry/pickup) - bez opakowania
			// nie ma czego pokazać w slotach/podsumowaniu (patrz startFresh()
			// wyżej, gdzie dzieje się to samo dla pierwszego wejścia).
			if ( self.isPackageCounter() && ! self.currentPackage && self.packages.length ) {
				var defaultSlug = self.root.dataset.defaultPackage;
				var pkg = defaultSlug
					? self.packages.filter( function ( p ) { return p.slug === defaultSlug; } )[ 0 ]
					: null;
				self.setPackage( pkg || self.packages[ 0 ], true );
			}

			// Natychmiastowa, płynna informacja "ładuję" (przygaszenie) -
			// samo tło i produkty podmieniają się dopiero razem, gdy dane
			// nowej lady faktycznie dotrą (patrz renderProducts()), więc
			// nigdy nie widać nowego tła z produktami poprzedniej lady.
			if ( self.els.productsGrid ) {
				self.els.productsGrid.classList.add( 'is-counter-loading' );
			}
			self.renderSlots();
			self.renderSummary();
			self.loadProducts();
		} );

		this.applyCounterBackground();
	};

	OmniVirtualCounter.prototype.getCurrentCounterMode = function () {
		return this.currentCounter && this.currentCounter.mode ? this.currentCounter.mode : 'package';
	};

	OmniVirtualCounter.prototype.isPackageCounter = function () {
		return 'package' === this.getCurrentCounterMode();
	};

	OmniVirtualCounter.prototype.isInquiryCounter = function () {
		return 'inquiry_single' === this.getCurrentCounterMode();
	};

	/**
	 * Niezależnie od trybu lady (package/inquiry_single/pickup_list) - admin
	 * może włączyć "Przegląd" dla dowolnej lady (Ladach > Układy, patrz
	 * Counter_Layouts::get_counter_overview_first()). Wtedy strona 1 =
	 * przegląd WSZYSTKICH produktów (siatka jak na ladzie domyślnej, na jej
	 * tle - patrz getBackgroundCounter()). Tak samo, gdy klient akurat
	 * przegląda wyniki wyszukiwania/filtra - REST i tak zawsze zwraca wtedy
	 * zwykłą, chunkowaną listę (patrz Products::query_products() -
	 * $counter_filter_browse), nigdy pojedynczy "widok szczegółowy"
	 * powiązany z numerem strony.
	 */
	OmniVirtualCounter.prototype.isInquiryOverview = function () {
		return !! ( this.currentCounter && this.currentCounter.overview_first ) && ( 1 === this.page || this.hasActiveProductFilter() );
	};

	/**
	 * Lada z włączonym "Przeglądem": strony 2, 3, 4... - każda pokazuje
	 * dokładnie JEDEN, odpowiadający produkt (kolejny wg sortowania
	 * Strona/Miejsce w Ladach > Układy) w dużym widoku pojedynczego
	 * produktu - dodanie/zamówienie działa dalej dokładnie tak, jak w
	 * zwykłym trybie tej lady (patrz handleAddClick()).
	 */
	OmniVirtualCounter.prototype.isInquiryDetail = function () {
		return !! ( this.currentCounter && this.currentCounter.overview_first ) && this.page > 1 && ! this.hasActiveProductFilter();
	};

	OmniVirtualCounter.prototype.isPickupCounter = function () {
		return 'pickup_list' === this.getCurrentCounterMode();
	};

	/**
	 * Najwcześniejsza dopuszczalna data (YYYY-MM-DD) = dziś + leadDays dni
	 * kalendarzowych - wspólna logika dla pola "Data odbioru" tortu
	 * (CFG.settings.leadTimeCakeDays) i pola "Termin realizacji" pozostałych
	 * lad (CFG.settings.leadTimeDefaultDays).
	 *
	 * @param {number} leadDays Minimalna liczba dni wyprzedzenia.
	 * @returns {string}
	 */
	OmniVirtualCounter.prototype.getMinDateString = function ( leadDays ) {
		var date = new Date();
		date.setDate( date.getDate() + ( parseInt( leadDays, 10 ) || 0 ) );
		return date.toISOString().slice( 0, 10 );
	};

	/**
	 * Waliduje pole "Termin realizacji" (konfigurator zestawów / lista
	 * odbioru pieczywa) przed wysyłką - backend i tak zawsze przelicza
	 * minimalny termin od nowa (patrz Cart::validate_order_date()), to tylko
	 * szybki feedback dla klienta bez czekania na odpowiedź serwera.
	 *
	 * @returns {boolean}
	 */
	OmniVirtualCounter.prototype.validateOrderDate = function () {
		if ( ! this.els.orderDate ) {
			return true;
		}

		var value = this.els.orderDate.value;

		if ( ! value ) {
			this.pushNotice( 'warning', 'Wybierz termin realizacji zamówienia.' );
			return false;
		}

		if ( this.els.orderDate.min && value < this.els.orderDate.min ) {
			this.pushNotice( 'warning', 'Wybrany termin jest zbyt wczesny - wybierz późniejszą datę.' );
			return false;
		}

		return true;
	};

	OmniVirtualCounter.prototype.getProductsPerPageForCurrentCounter = function () {
		if ( this.isPickupCounter() ) {
			return 2;
		}

		return this.productsPerPage;
	};

	OmniVirtualCounter.prototype.updateCounterModeUI = function () {
		var mode = this.getCurrentCounterMode();

		this.root.classList.remove( 'is-counter-mode-package', 'is-counter-mode-inquiry_single', 'is-counter-mode-pickup_list' );
		this.root.classList.add( 'is-counter-mode-' + mode );
		this.root.classList.toggle( 'is-inquiry-detail', this.isInquiryDetail() );

		if ( this.els.deliveryMethod ) {
			// Wysyłka może być globalnie ukryta w Ustawieniach ("Pokaż wysyłkę
			// jako opcję dostawy") - wtedy klient widzi tylko odbiór osobisty.
			this.els.deliveryMethod.hidden = ! this.isPickupCounter() || ! CFG.settings.enableShipping;
		}

		if ( this.els.inquiryDetailBar ) {
			this.els.inquiryDetailBar.hidden = ! this.isInquiryDetail();
		}

		if ( this.els.orderDateWrap ) {
			// Lada tortów ma własne pole "Data odbioru" w formularzu zapytania
			// (inna, dłuższa reguła wyprzedzenia - patrz showInquiryForm()); to
			// pole obsługuje pozostałe lady (konfigurator zestawów, lista
			// odbioru pieczywa).
			this.els.orderDateWrap.hidden = this.isInquiryCounter();
		}
		if ( this.els.orderDate ) {
			this.els.orderDate.min = this.getMinDateString( CFG.settings.leadTimeDefaultDays );
		}

		if ( this.isPickupCounter() ) {
			// setPickupDeliveryMethod() also sets the add-to-cart button text
			// for the current method, so no separate assignment is needed here.
			this.setPickupDeliveryMethod( 'pickup' );
		} else if ( this.els.addToCart ) {
			this.els.addToCart.textContent = CFG.i18n.addToCartText || 'Dodaj do koszyka';
		}

		if ( this.els.addToCart ) {
			this.els.addToCart.disabled = ! this.isPickupCounter() || ! this.pickupItems.length;
		}

		if ( this.els.emptyHint ) {
			this.els.emptyHint.textContent = this.isPickupCounter()
				? 'Dodaj produkty pieczywa do listy odbioru.'
				: ( CFG.i18n.emptyPackageText || '' );
			this.els.emptyHint.hidden = this.isInquiryCounter() || ( this.isPickupCounter() && this.pickupItems.length > 0 );
		}

		this.updateInquiryFormVisibility();
	};

	OmniVirtualCounter.prototype.applyCounterBackground = function () {
		var imageWidth;
		var imageHeight;

		if ( ! this.els.productsGrid || ! this.currentCounter ) {
			return;
		}

		var bgCounter = this.getBackgroundCounter();

		if ( bgCounter.image_url ) {
			imageWidth = parseInt( bgCounter.image_width || 1448, 10 ) || 1448;
			imageHeight = parseInt( bgCounter.image_height || 1086, 10 ) || 1086;
			this.els.productsGrid.style.setProperty( 'background-image', 'url("' + bgCounter.image_url + '")', 'important' );
			this.els.productsGrid.style.setProperty( '--omni-vc-counter-aspect-ratio', imageWidth + ' / ' + imageHeight );
			this.els.productsGrid.classList.add( 'has-counter-background' );
		} else {
			this.els.productsGrid.style.removeProperty( 'background-image' );
			this.els.productsGrid.style.removeProperty( '--omni-vc-counter-aspect-ratio' );
			this.els.productsGrid.classList.remove( 'has-counter-background' );
		}
	};

	/**
	 * Przegląd wszystkich tortów (strona 1 lady z przeglądem) może mieć
	 * osobne tło. Widok pojedynczego tortu (strona 2+) może brać tło z lady
	 * pokazowej, ale pozycja i rozmiar produktu zawsze zostają layoutem
	 * konkretnego tortu na głównej ladzie.
	 *
	 * @return {Object} Obiekt lady (patrz REST /counters), którego image_url
	 *   ma być użyte jako tło.
	 */
	OmniVirtualCounter.prototype.getBackgroundCounter = function () {
		if ( this.isInquiryOverview() ) {
			if ( this.currentCounter.overview_image_url ) {
				return {
					image_url: this.currentCounter.overview_image_url,
					image_width: this.currentCounter.overview_image_width || this.currentCounter.image_width,
					image_height: this.currentCounter.overview_image_height || this.currentCounter.image_height,
				};
			}

			var defaultCounter = this.counters.filter( function ( counter ) {
				return !! counter.is_default;
			} )[ 0 ];

			if ( defaultCounter ) {
				return defaultCounter;
			}
		}

		if ( this.isInquiryDetail() ) {
			return this.getDetailTemplateCounter();
		}

		return this.currentCounter;
	};

	OmniVirtualCounter.prototype.getProductCounterLayoutForSlug = function ( product, slug ) {
		var layouts = product.counter_layouts || {};
		var layout = slug && layouts[ slug ] ? layouts[ slug ] : null;

		if ( ! layout ) {
			return {
				enabled: false,
				image_id: 0,
				page: 1,
				slot: 1,
				x: 50,
				y: 52,
				width: 13,
				rotation: 0,
				z_index: 10,
				badge: null,
				add_button: null,
				description_button: null,
			};
		}

		return layout;
	};

	OmniVirtualCounter.prototype.getBaseProductCounterLayout = function ( product ) {
		var slug = this.currentCounter ? this.currentCounter.slug : '';

		return this.getProductCounterLayoutForSlug( product, slug );
	};

	OmniVirtualCounter.prototype.getProductCounterLayout = function ( product ) {
		var baseLayout = this.getBaseProductCounterLayout( product );
		var detailLayout;
		var mergedLayout;

		if ( this.isInquiryDetail() && baseLayout && baseLayout.detail ) {
			detailLayout = baseLayout.detail;
			mergedLayout = Object.assign( {}, baseLayout, detailLayout );
			mergedLayout.enabled = baseLayout.enabled;
			mergedLayout.page = baseLayout.page;
			mergedLayout.image_url = parseInt( detailLayout.image_id, 10 ) > 0 ? detailLayout.image_url || '' : baseLayout.image_url || '';
			return mergedLayout;
		}

		return baseLayout;
	};

	OmniVirtualCounter.prototype.getCounterSlotFrame = function ( slot ) {
		var index = Math.max( 1, Math.min( 8, parseInt( slot, 10 ) || 1 ) ) - 1;
		var col = index % 4;
		var row = Math.floor( index / 4 );
		var xs = [ 20, 40, 60, 80 ];
		var labelTops = [ 40.8, 69.2 ];
		var buttonTops = [ 45.2, 73.6 ];
		var statusTops = [ 36.8, 65.2 ];

		return {
			left: xs[ col ],
			labelTop: labelTops[ row ],
			buttonTop: buttonTops[ row ],
			statusTop: statusTops[ row ],
		};
	};

	OmniVirtualCounter.prototype.getCounterSlotPosition = function ( slot ) {
		var index = Math.max( 1, Math.min( 8, parseInt( slot, 10 ) || 1 ) ) - 1;
		var col = index % 4;
		var row = Math.floor( index / 4 );
		var xs = [ 20, 40, 60, 80 ];
		var ys = [ 30, 58 ];

		return {
			x: xs[ col ],
			y: ys[ row ],
		};
	};

	OmniVirtualCounter.prototype.getDefaultControlLayout = function ( slot ) {
		var slotFrame = this.getCounterSlotFrame( slot );

		// Duży, wyśrodkowany układ dotyczy tylko widoku pojedynczego tortu -
		// w przeglądzie wszystkich tortów (siatka) kontrolki mają pozycje
		// per-slot, jak na pozostałych ladach.
		if ( this.isInquiryDetail() ) {
			return this.getInquiryControlLayout();
		}

		return {
			badge: { x: slotFrame.left, y: slotFrame.labelTop, width: 18, height: 3.8 },
			add_button: { x: Math.max( 0, slotFrame.left - 4.5 ), y: slotFrame.buttonTop, width: 8.4, height: 2.7 },
			description_button: { x: Math.min( 100, slotFrame.left + 4.5 ), y: slotFrame.buttonTop, width: 8.4, height: 2.7 },
			status_badge: { x: slotFrame.left, y: slotFrame.statusTop },
		};
	};

	OmniVirtualCounter.prototype.getInquiryControlLayout = function () {
		return {
			badge: { x: 50, y: 69.9, width: 30, height: 4.4 },
			add_button: { x: 45.2, y: 81.4, width: 12, height: 3.1 },
			description_button: { x: 54.8, y: 81.4, width: 12, height: 3.1 },
			status_badge: { x: 50, y: 66.8 },
		};
	};

	OmniVirtualCounter.prototype.shouldUseInquiryControlLayout = function ( layout ) {
		return ! layout || ! layout.badge || ! layout.add_button || ! layout.description_button;
	};

	OmniVirtualCounter.prototype.applyCounterModeLayout = function ( layout ) {
		var normalized = Object.assign( {}, layout || {} );

		return normalized;
	};

	OmniVirtualCounter.prototype.normalizeControlLayout = function ( control, defaults ) {
		var source = control || {};

		return {
			x: parseFloat( 'undefined' !== typeof source.x ? source.x : defaults.x ),
			y: parseFloat( 'undefined' !== typeof source.y ? source.y : defaults.y ),
			width: parseFloat( 'undefined' !== typeof source.width ? source.width : defaults.width ),
			height: parseFloat( 'undefined' !== typeof source.height ? source.height : defaults.height ),
		};
	};

	OmniVirtualCounter.prototype.hasActiveProductFilter = function () {
		return ( parseInt( this.currentCategory, 10 ) || 0 ) > 0 || '' !== String( this.searchTerm || '' ).trim();
	};

	OmniVirtualCounter.prototype.shouldShowCategoryFilters = function () {
		var slug = this.currentCounter && this.currentCounter.slug ? this.currentCounter.slug : '';

		return -1 === COUNTERS_WITHOUT_CATEGORY_FILTERS.indexOf( slug );
	};

	OmniVirtualCounter.prototype.getCounterSlotTemplate = function ( slot ) {
		var targetSlot = Math.max( 1, Math.min( 8, parseInt( slot, 10 ) || 1 ) );
		var templates = this.currentCounter && this.currentCounter.slot_templates ? this.currentCounter.slot_templates : {};
		var template = templates[ targetSlot ] || templates[ String( targetSlot ) ] || null;

		if ( ! template ) {
			var position = this.getCounterSlotPosition( targetSlot );
			var controls = this.getDefaultControlLayout( targetSlot );
			template = {
				slot: targetSlot,
				x: position.x,
				y: position.y,
				width: 13,
				rotation: 0,
				z_index: 10,
				badge: controls.badge,
				add_button: controls.add_button,
				description_button: controls.description_button,
			};
		}

		return {
			slot: targetSlot,
			x: parseFloat( template.x || 50 ),
			y: parseFloat( template.y || 52 ),
			width: parseFloat( template.width || 13 ),
			rotation: parseFloat( template.rotation || 0 ),
			z_index: parseInt( template.z_index || 10, 10 ) || 10,
			badge: Object.assign( {}, template.badge || {} ),
			add_button: Object.assign( {}, template.add_button || {} ),
			description_button: Object.assign( {}, template.description_button || {} ),
		};
	};

	OmniVirtualCounter.prototype.getDisplayProductLayout = function ( product, index, filteredMode ) {
		var source = this.getProductCounterLayout( product );
		var layout = Object.assign( {}, source );
		var displaySlot;
		var template;

		if ( filteredMode ) {
			displaySlot = ( index % 8 ) + 1;
			template = this.getCounterSlotTemplate( displaySlot );
			layout.slot = displaySlot;
			layout.x = template.x;
			layout.y = template.y;
			layout.width = template.width;
			layout.rotation = template.rotation;
			layout.badge = template.badge;
			layout.add_button = template.add_button;
			layout.description_button = template.description_button;
			layout.z_index = template.z_index + index;
		}

		return this.applyCounterModeLayout( layout );
	};

	OmniVirtualCounter.prototype.renderCategories = function () {
		if ( ! this.els.categories ) {
			return;
		}

		var self = this;
		var shouldShowCategories = this.shouldShowCategoryFilters();
		var hiddenCategorySlugs = HIDDEN_CATEGORY_SLUGS_BY_COUNTER[ this.currentCounter && this.currentCounter.slug ? this.currentCounter.slug : '' ] || [];
		var visibleCategories = this.categories.filter( function ( cat ) {
			var keys = [
				normalizeCategoryKey( cat.slug ),
				normalizeCategoryKey( cat.name ),
			];

			return ! hiddenCategorySlugs.some( function ( hiddenSlug ) {
				return -1 !== keys.indexOf( normalizeCategoryKey( hiddenSlug ) );
			} );
		} );
		var visibleCategoryIds = visibleCategories.map( function ( cat ) {
			return parseInt( cat.id, 10 ) || 0;
		} );

		this.els.categories.hidden = ! shouldShowCategories;
		if ( this.els.categoryFilter ) {
			this.els.categoryFilter.hidden = ! shouldShowCategories;
		}

		if ( ! shouldShowCategories || ( this.currentCategory > 0 && -1 === visibleCategoryIds.indexOf( this.currentCategory ) ) ) {
			this.currentCategory = 0;
			this.page = 1;
		}

		this.els.categories.querySelectorAll( '.omni-vc-category-pill:not([data-category="0"])' ).forEach( function ( btn ) {
			btn.remove();
		} );
		if ( this.els.categorySelect ) {
			this.els.categorySelect.innerHTML = '';
			var allOption = document.createElement( 'option' );
			allOption.value = '0';
			allOption.textContent = this.els.categories.querySelector( '[data-category="0"]' ).textContent;
			this.els.categorySelect.appendChild( allOption );
		}

		visibleCategories.forEach( function ( cat ) {
			var btn = document.createElement( 'button' );
			btn.type = 'button';
			btn.className = 'omni-vc-category-pill' + ( self.currentCategory === parseInt( cat.id, 10 ) ? ' is-active' : '' );
			btn.dataset.category = String( cat.id );
			btn.setAttribute( 'role', 'tab' );
			btn.setAttribute( 'aria-selected', self.currentCategory === parseInt( cat.id, 10 ) ? 'true' : 'false' );
			btn.textContent = cat.name;
			self.els.categories.appendChild( btn );
			if ( self.els.categorySelect ) {
				var option = document.createElement( 'option' );
				option.value = String( cat.id );
				option.textContent = cat.name;
				self.els.categorySelect.appendChild( option );
			}
		} );
		if ( this.els.categorySelect ) {
			this.els.categorySelect.value = String( this.currentCategory );
			if ( ! this.categorySelectListenerBound ) {
				this.els.categorySelect.addEventListener( 'change', function () {
					var selected = Array.prototype.find.call( self.els.categories.querySelectorAll( '.omni-vc-category-pill' ), function ( btn ) {
						return btn.dataset.category === self.els.categorySelect.value;
					} );
					if ( selected ) {
						selected.click();
					} else {
						self.els.categorySelect.value = String( self.currentCategory );
					}
				} );
				this.categorySelectListenerBound = true;
			}
		}

		if ( ! this.categoriesListenerBound ) {
			this.els.categories.addEventListener( 'click', function ( e ) {
				var btn = e.target.closest( '.omni-vc-category-pill' );
				if ( ! btn ) {
					return;
				}
				self.els.categories.querySelectorAll( '.omni-vc-category-pill' ).forEach( function ( b ) {
					b.classList.remove( 'is-active' );
					b.setAttribute( 'aria-selected', 'false' );
				} );
				btn.classList.add( 'is-active' );
				btn.setAttribute( 'aria-selected', 'true' );
				self.currentCategory = parseInt( btn.dataset.category, 10 ) || 0;
				if ( self.els.categorySelect ) {
					self.els.categorySelect.value = String( self.currentCategory );
				}
				self.page = 1;
				self.loadProducts();
			} );
			this.categoriesListenerBound = true;
		}

		this.els.categories.querySelectorAll( '.omni-vc-category-pill' ).forEach( function ( btn ) {
			var isActive = ( parseInt( btn.dataset.category, 10 ) || 0 ) === self.currentCategory;
			btn.classList.toggle( 'is-active', isActive );
			btn.setAttribute( 'aria-selected', isActive ? 'true' : 'false' );
		} );
	};

	OmniVirtualCounter.prototype.renderPackageOptions = function () {
		var self = this;
		this.els.packageSelect.innerHTML = '';

		this.packages.forEach( function ( pkg ) {
			var opt = document.createElement( 'option' );
			opt.value = String( pkg.id );
			opt.textContent = pkg.name + ' (' + pkg.slots + ' ' + ( CFG.i18n.slotsLabel || 'miejsc' ).toLowerCase() + ')';
			self.els.packageSelect.appendChild( opt );
		} );

		this.renderPackagePicker();
	};

	OmniVirtualCounter.prototype.renderPackagePicker = function () {
		var self = this;

		if ( ! this.els.packagePicker || ! this.els.packageCards ) {
			return;
		}

		this.els.packageCards.innerHTML = '';

		if ( this.packages.length <= 1 ) {
			this.els.packagePicker.hidden = true;
			return;
		}

		this.els.packagePicker.hidden = false;

		var totalPages = Math.max( 1, Math.ceil( this.packages.length / this.packagePageSize ) );
		if ( this.packagePage > totalPages ) {
			this.packagePage = totalPages;
		}
		if ( this.packagePage < 1 ) {
			this.packagePage = 1;
		}

		var start = ( this.packagePage - 1 ) * this.packagePageSize;
		var pageItems = this.packages.slice( start, start + this.packagePageSize );

		pageItems.forEach( function ( pkg ) {
			var btn = document.createElement( 'button' );
			btn.type = 'button';
			btn.className = 'omni-vc-package-card' + ( self.currentPackage && pkg.id === self.currentPackage.id ? ' is-active' : '' );
			btn.dataset.packageId = String( pkg.id );
			btn.setAttribute( 'aria-pressed', self.currentPackage && pkg.id === self.currentPackage.id ? 'true' : 'false' );
			btn.innerHTML =
				'<span class="omni-vc-package-card-name"></span>' +
				'<span class="omni-vc-package-card-meta"></span>';
			btn.querySelector( '.omni-vc-package-card-name' ).textContent = pkg.name;
			btn.querySelector( '.omni-vc-package-card-meta' ).textContent =
				pkg.slots + ' ' + ( CFG.i18n.slotsLabel || 'miejsc' ).toLowerCase() + ' / ' + formatPrice( pkg.price || 0, self.currency );
			btn.addEventListener( 'click', function () {
				self.handlePackageChange( pkg.id );
			} );
			self.els.packageCards.appendChild( btn );
		} );

		if ( this.els.packagePager ) {
			this.els.packagePager.hidden = totalPages <= 1;
		}
		if ( this.els.packagePrev ) {
			this.els.packagePrev.disabled = this.packagePage <= 1;
		}
		if ( this.els.packageNext ) {
			this.els.packageNext.disabled = this.packagePage >= totalPages;
		}
		if ( this.els.packagePageStatus ) {
			this.els.packagePageStatus.textContent = this.packagePage + ' / ' + totalPages;
		}
	};

	OmniVirtualCounter.prototype.loadProducts = function () {
		var self = this;
		var requestSeq = ++this.productsRequestSeq;

		apiGet( 'products', {
			category: this.currentCategory || undefined,
			search: this.searchTerm || undefined,
			page: this.page,
			per_page: this.getProductsPerPageForCurrentCounter(),
			counter: this.currentCounter ? this.currentCounter.slug : undefined,
		} )
			.then( function ( res ) {
				if ( requestSeq !== self.productsRequestSeq ) {
					return;
				}

				if ( ! res.ok ) {
					throw new Error( 'GET /products failed with status ' + res.status );
				}
				self.pages = res.data.pages || 1;
				// Na ladzie inquiry_single strona sama w sobie decyduje, czy to
				// przegląd (1) czy widok pojedynczego tortu (2+) - patrz
				// isInquiryOverview()/isInquiryDetail() - stąd odświeżenie UI
				// trybu (tło, pasek "wstecz", widoczność wyszukiwarki) przy
				// KAŻDEJ zmianie strony, nie tylko przy zmianie lady.
				self.updateCounterModeUI();
				self.renderProducts( toArray( res.data.items ) );
				self.renderPagination();
				self.renderSummary();
			} )
			.catch( function ( err ) {
				if ( requestSeq !== self.productsRequestSeq ) {
					return;
				}

				self.reportFatalError( err );
				self.renderProducts( [] );
			} );
	};

	OmniVirtualCounter.prototype.renderProducts = function ( items ) {
		var self = this;
		var direction = this.page >= this.previousPage ? 'is-slide-next' : 'is-slide-prev';
		var filteredMode;

		this.currentProducts = toArray( items );

		if ( this.isInquiryDetail() ) {
			// Widok pojedynczego tortu (strona 2+): REST zwraca dokladnie ten
			// jeden produkt (patrz Products::query_products()), a jego wlasny
			// uklad na tej ladzie (bez auto-siatki) to dotychczasowy, duzy
			// widok pojedynczego produktu.
			filteredMode = false;
		} else if ( this.isInquiryOverview() && ! this.hasActiveProductFilter() ) {
			// Strona 1 przeglądu tortów używa zapisanych pozycji/wielkości z
			// głównej Lady tortów. Automatyczne przepakowanie 1-8 zostaje tylko
			// dla wyników kategorii/szukania, gdzie produkty mogą pochodzić z
			// różnych stron i muszą dostać tymczasowe miejsca.
			filteredMode = false;
		} else {
			filteredMode = this.hasActiveProductFilter();
		}

		var visibleItems = this.currentProducts.filter( function ( product ) {
			return false !== self.getBaseProductCounterLayout( product ).enabled;
		} );

		this.els.productsGrid.innerHTML = '';
		this.els.productsGrid.classList.remove( 'is-slide-next', 'is-slide-prev', 'is-counter-loading' );
		void this.els.productsGrid.offsetWidth;
		this.els.productsGrid.classList.add( direction );
		// Tło lady zmienia się TUTAJ, w tym samym takcie co podmiana
		// produktów - nigdy wcześniej (np. zaraz po kliknięciu zakładki) -
		// żeby oba elementy zawsze przechodziły razem w jednej animacji
		// (patrz .is-slide-next/.is-slide-prev), bez efektu "tło już inne,
		// produkty jeszcze stare" w trakcie oczekiwania na REST.
		this.applyCounterBackground();
		this.previousPage = this.page;

		if ( ! visibleItems.length ) {
			var empty = document.createElement( 'p' );
			empty.className = 'omni-vc-empty-state';
			empty.textContent = CFG.i18n.noProducts;
			this.els.productsGrid.appendChild( empty );
			return;
		}

		visibleItems.forEach( function ( product, index ) {
			self.els.productsGrid.appendChild( self.buildProductCard( product, self.getDisplayProductLayout( product, index, filteredMode ) ) );
		} );
	};

	OmniVirtualCounter.prototype.buildProductCard = function ( product, displayLayout ) {
		var node = this.els.tmplProduct.content.firstElementChild.cloneNode( true );
		var self = this;
		var layout = this.applyCounterModeLayout( displayLayout || this.getProductCounterLayout( product ) );
		var controlDefaults = this.getDefaultControlLayout( layout.slot );
		var badgeLayout = this.normalizeControlLayout( layout.badge, controlDefaults.badge );
		var addButtonLayout = this.normalizeControlLayout( layout.add_button, controlDefaults.add_button );
		var descriptionButtonLayout = this.normalizeControlLayout( layout.description_button, controlDefaults.description_button );

		node.dataset.productId = String( product.id );
		node.style.setProperty( '--omni-vc-item-left', layout.x + '%' );
		node.style.setProperty( '--omni-vc-item-top', layout.y + '%' );
		node.style.setProperty( '--omni-vc-item-width', layout.width + '%' );
		node.style.setProperty( '--omni-vc-item-height', Math.max( 8, layout.width * 0.9 ) + '%' );
		node.style.setProperty( '--omni-vc-slot-control-left', badgeLayout.x + '%' );
		node.style.setProperty( '--omni-vc-slot-label-top', badgeLayout.y + '%' );
		node.style.setProperty( '--omni-vc-slot-button-top', addButtonLayout.y + '%' );
		node.style.setProperty( '--omni-vc-slot-status-top', controlDefaults.status_badge.y + '%' );
		node.style.setProperty( '--omni-vc-product-badge-left', badgeLayout.x + '%' );
		node.style.setProperty( '--omni-vc-product-badge-top', badgeLayout.y + '%' );
		node.style.setProperty( '--omni-vc-product-badge-width', badgeLayout.width + '%' );
		node.style.setProperty( '--omni-vc-product-badge-height', badgeLayout.height + '%' );
		node.style.setProperty( '--omni-vc-product-add-left', addButtonLayout.x + '%' );
		node.style.setProperty( '--omni-vc-product-add-top', addButtonLayout.y + '%' );
		node.style.setProperty( '--omni-vc-product-add-width', addButtonLayout.width + '%' );
		node.style.setProperty( '--omni-vc-product-add-height', addButtonLayout.height + '%' );
		node.style.setProperty( '--omni-vc-product-description-left', descriptionButtonLayout.x + '%' );
		node.style.setProperty( '--omni-vc-product-description-top', descriptionButtonLayout.y + '%' );
		node.style.setProperty( '--omni-vc-product-description-width', descriptionButtonLayout.width + '%' );
		node.style.setProperty( '--omni-vc-product-description-height', descriptionButtonLayout.height + '%' );
		node.style.setProperty( '--omni-vc-item-photo-left', '50%' );
		node.style.setProperty( '--omni-vc-item-photo-top', '50%' );
		node.style.setProperty( '--omni-vc-item-photo-width', '100%' );
		node.style.setProperty( '--omni-vc-item-photo-height', '100%' );
		node.style.setProperty( '--omni-vc-item-photo-max-width', '100%' );
		node.style.setProperty( '--omni-vc-item-photo-max-height', '100%' );
		node.style.setProperty( '--omni-vc-item-rotation', layout.rotation + 'deg' );
		node.style.zIndex = String( layout.z_index || 10 );
		var isShippableHere = this.isPickupCounter() && !! product.shipping_allowed && !! CFG.settings.enableShipping;
		node.classList.toggle( 'is-shippable', isShippableHere );
		node.querySelector( '.omni-vc-product-name' ).textContent = product.name + ( isShippableHere ? ' 🚚' : '' );
		this.detachCounterControlsFromMedia( node );
		var img = node.querySelector( '.omni-vc-product-thumb' );
		withImageFallback( img, PRODUCT_IMG_FALLBACK );
		img.src = layout.image_url || product.thumbnail || PRODUCT_IMG_FALLBACK;
		img.alt = product.name;

		var linkEl = node.querySelector( '.omni-vc-product-link' );
		if ( linkEl ) {
			if ( product.permalink ) {
				linkEl.href = product.permalink;
			} else {
				linkEl.hidden = true;
			}
		}

		var priceEl = node.querySelector( '.omni-vc-product-price' );
		if ( CFG.settings.showPrices ) {
			priceEl.textContent = formatPrice( product.price, this.currency );
		} else {
			priceEl.hidden = true;
		}

		var available = !! product.purchasable && ( product.stock_status === 'instock' || product.stock_status === 'onbackorder' );
		var packageAllowed = ! this.isPackageCounter() || this.isProductAllowedInCurrentPackage( product );
		var statusBadge = node.querySelector( '.omni-vc-product-badge' );
		var purchaseMode = ! this.isInquiryCounter();
		var updateAvailabilityBadge = function () {
			var selectedVariation = variationSelect && ! variationSelect.hidden
				? variationSelect.options[ variationSelect.selectedIndex ]
				: null;
			var stockStatus = selectedVariation ? selectedVariation.dataset.stockStatus : product.stock_status;
			var canPurchase = selectedVariation ? selectedVariation.dataset.available === '1' : available;
			var blocked = purchaseMode && ( ! canPurchase || ! packageAllowed );
			var label = ! canPurchase || stockStatus === 'outofstock'
				? CFG.i18n.unavailable
				: ! packageAllowed && purchaseMode
					? CFG.i18n.unavailableInPackage
					: stockStatus === 'onbackorder'
						? CFG.i18n.onBackorder
						: CFG.i18n.available;

			statusBadge.textContent = label;
			statusBadge.hidden = label === CFG.i18n.available;
			statusBadge.classList.toggle( 'is-available', label === CFG.i18n.available );
			statusBadge.classList.toggle( 'is-backorder', label === CFG.i18n.onBackorder );
			statusBadge.classList.toggle( 'is-unavailable', label === CFG.i18n.unavailable || label === CFG.i18n.unavailableInPackage );
			node.classList.toggle( 'is-out-of-stock', ! canPurchase || stockStatus === 'outofstock' );
			node.classList.toggle( 'is-unavailable', blocked );
			node.querySelector( '.omni-vc-product-add' ).disabled = blocked;
			img.draggable = this.isPackageCounter() && CFG.settings.enableDragDrop && ! blocked;
		};

		var variationSelect = node.querySelector( '.omni-vc-product-variation' );
		var variations = toArray( product.variations );
		if ( product.is_variable && variations.length ) {
			variationSelect.hidden = false;
			variations.forEach( function ( v ) {
				var opt = document.createElement( 'option' );
				opt.value = String( v.variation_id );
				var attrs = Object.values( v.attributes || {} ).join( ', ' );
				opt.textContent = ( attrs || ( CFG.i18n.productsLabel || 'Wariant' ) ) + ' - ' + formatPrice( v.price, self.currency );
				opt.dataset.price = v.price;
				opt.dataset.thumbnail = v.thumbnail;
				opt.dataset.available = v.purchasable ? '1' : '0';
				opt.dataset.stockStatus = v.stock_status;
				variationSelect.appendChild( opt );
			} );
			variationSelect.addEventListener( 'change', updateAvailabilityBadge.bind( this ) );
		}
		updateAvailabilityBadge.call( this );

		if ( this.isInquiryOverview() && ! this.hasActiveProductFilter() ) {
			// Przegląd wszystkich tortów (strona 1, bez wyszukiwania): przycisk
			// (i cała karta) najpierw otwiera duży widok tego tortu - o
			// zamówienie klient pyta dopiero stamtąd (patrz isInquiryDetail()
			// poniżej). Podczas wyszukiwania nie ma osobnej "strony
			// szczegółów" (patrz activate() niżej), więc przycisk zachowuje
			// się jak w widoku pojedynczego produktu.
			node.querySelector( '.omni-vc-product-add' ).textContent = 'Zobacz';
		} else if ( this.isInquiryCounter() ) {
			node.querySelector( '.omni-vc-product-add' ).textContent = 'Ustal szczegóły zamówienia';
		} else if ( this.isPickupCounter() ) {
			node.querySelector( '.omni-vc-product-add' ).textContent = '+ Dodaj do listy';
		}

		if ( ! CFG.settings.enableAddButton && this.isPackageCounter() ) {
			node.querySelector( '.omni-vc-product-add' ).hidden = true;
		}

		if ( this.isPackageCounter() && CFG.settings.enableDragDrop && available && packageAllowed ) {
			img.draggable = ! node.classList.contains( 'is-unavailable' );
			this.bindDraggable( img, function () {
				return self.resolveDragPayload( node, product, variationSelect );
			} );
		} else {
			img.draggable = false;
		}

		function activate() {
			if ( self.isInquiryOverview() && ! self.hasActiveProductFilter() ) {
				self.openInquiryDetail( product );
				return;
			}
			// Wyniki wyszukiwania/filtra nie mają osobnego numeru "strony
			// szczegółów" (płaska lista dopasowań, patrz
			// Products::query_products()) - klik od razu otwiera formularz
			// zapytania, tak jak w widoku pojedynczego tortu.
			self.handleAddClick( node, product, variationSelect );
		}

		node.querySelector( '.omni-vc-product-add' ).addEventListener( 'click', function ( event ) {
			event.stopPropagation();
			activate();
		} );

		if ( this.isInquiryOverview() ) {
			// Cała karta (nie tylko przycisk) otwiera widok tortu - klikanie w
			// zdjęcie jest naturalnym gestem w przeglądzie całej lady.
			node.classList.add( 'is-clickable' );
			node.addEventListener( 'click', function ( event ) {
				if ( event.target.closest( '.omni-vc-product-add, .omni-vc-product-link, .omni-vc-product-variation' ) ) {
					return;
				}
				activate();
			} );
		}

		node.addEventListener( 'keydown', function ( e ) {
			if ( ( 'Enter' === e.key || ' ' === e.key ) && e.target === node ) {
				e.preventDefault();
				activate();
			}
		} );

		return node;
	};

	/**
	 * Otwiera duży widok jednego, wybranego produktu z przeglądu wszystkich
	 * produktów - to zwykła nawigacja do strony tego produktu (patrz
	 * detail_page doklejane przez Products::query_products() do każdego
	 * produktu na stronie 1), więc "wstecz"/"dalej" w paginacji działa
	 * identycznie jak klik w kartę.
	 *
	 * @param {Object} product Produkt zwrócony przez REST /products (strona 1).
	 */
	OmniVirtualCounter.prototype.openInquiryDetail = function ( product ) {
		var targetPage = parseInt( product.detail_page, 10 ) || 2;

		// Formularz zapytania (jeśli otwarty dla innego produktu, tryb
		// inquiry_single) nie ma tu już zastosowania - klient zacznie od
		// nowa przyciskiem pod tym produktem.
		this.inquiryProduct = null;
		this.previousPage = this.page;
		this.page = targetPage;
		this.loadProducts();
	};

	/**
	 * Wraca z widoku pojedynczego tortu do przeglądu wszystkich tortów
	 * (strona 1) - to samo, co klik w przycisk "1" w paginacji.
	 */
	OmniVirtualCounter.prototype.closeInquiryDetail = function () {
		if ( 1 === this.page ) {
			return;
		}

		this.inquiryProduct = null;
		this.previousPage = this.page;
		this.page = 1;
		this.loadProducts();
	};

	OmniVirtualCounter.prototype.detachCounterControlsFromMedia = function ( node ) {
		var media = node.querySelector( '.omni-vc-product-media' );
		var statusBadge = node.querySelector( '.omni-vc-product-badge' );
		var descriptionLink = node.querySelector( '.omni-vc-product-link' );

		if ( ! media ) {
			return;
		}

		if ( statusBadge && statusBadge.parentNode === media ) {
			node.appendChild( statusBadge );
		}

		if ( descriptionLink && descriptionLink.parentNode === media ) {
			node.appendChild( descriptionLink );
		}

		if ( statusBadge ) {
			statusBadge.classList.add( 'omni-vc-product-status-control' );
		}

		if ( descriptionLink ) {
			descriptionLink.classList.add( 'omni-vc-product-description-control' );
		}
	};

	OmniVirtualCounter.prototype.resolveDragPayload = function ( node, product, variationSelect ) {
		var variationId = 0,
			price = product.price,
			thumbnail = product.thumbnail;

		if ( variationSelect && ! variationSelect.hidden && variationSelect.value ) {
			variationId = parseInt( variationSelect.value, 10 ) || 0;
			var opt = variationSelect.options[ variationSelect.selectedIndex ];
			if ( opt ) {
				price = parseFloat( opt.dataset.price ) || price;
				thumbnail = opt.dataset.thumbnail || thumbnail;
			}
		}

		return {
			product_id: product.id,
			variation_id: variationId,
			name: product.name,
			price: price,
			thumbnail: thumbnail,
			slots_required: parseInt( product.slots_required, 10 ) || 1,
			shipping_allowed: !! product.shipping_allowed,
		};
	};

	OmniVirtualCounter.prototype.handleAddClick = function ( node, product, variationSelect ) {
		if ( node.classList.contains( 'is-unavailable' ) ) {
			this.pushNotice( 'error', this.getProductRestrictionMessage( product ) );
			return;
		}
		var payload = this.resolveDragPayload( node, product, variationSelect );
		if ( this.isInquiryCounter() ) {
			this.showInquiryForm( payload );
			return;
		}
		if ( this.isPickupCounter() ) {
			this.addToPickupList( payload );
			return;
		}
		this.addToFirstFreeSlot( payload );
	};

	OmniVirtualCounter.prototype.isProductAllowedInCurrentPackage = function ( product ) {
		var pkg = this.currentPackage;

		if ( this.isAutoPackageSelectionEnabled() ) {
			return this.getPackageCandidates().some( function ( candidate ) {
				if ( hasCategoryIntersection( product.categories, candidate.disallowed_categories ) ) {
					return false;
				}

				if ( toArray( candidate.allowed_categories ).length && ! hasCategoryIntersection( product.categories, candidate.allowed_categories ) ) {
					return false;
				}

				return true;
			} );
		}

		if ( ! pkg ) {
			return true;
		}

		if ( hasCategoryIntersection( product.categories, pkg.disallowed_categories ) ) {
			return false;
		}

		if ( toArray( pkg.allowed_categories ).length && ! hasCategoryIntersection( product.categories, pkg.allowed_categories ) ) {
			return false;
		}

		return true;
	};

	OmniVirtualCounter.prototype.getProductRestrictionMessage = function ( product ) {
		if ( ! this.isProductAllowedInCurrentPackage( product ) ) {
			return this.isAutoPackageSelectionEnabled()
				? 'Produkt nie jest dozwolony w żadnym aktywnym opakowaniu: ' + product.name
				: 'Produkt nie jest dozwolony w wybranym opakowaniu: ' + product.name;
		}

		return CFG.i18n.outOfStock + ': ' + product.name;
	};

	OmniVirtualCounter.prototype.renderPagination = function () {
		var self = this;
		var prevBtn;
		var nextBtn;
		this.els.pagination.innerHTML = '';

		if ( this.pages <= 1 ) {
			return;
		}

		function goToPage( pageNum ) {
			var targetPage = Math.max( 1, Math.min( self.pages, parseInt( pageNum, 10 ) || 1 ) );
			if ( targetPage === self.page ) {
				return;
			}
			self.previousPage = self.page;
			self.page = targetPage;
			self.loadProducts();
		}

		prevBtn = document.createElement( 'button' );
		prevBtn.type = 'button';
		prevBtn.className = 'omni-vc-page-btn omni-vc-page-arrow';
		prevBtn.setAttribute( 'aria-label', 'Poprzednia strona' );
		prevBtn.textContent = '‹';
		prevBtn.disabled = this.page <= 1;
		prevBtn.addEventListener( 'click', function () {
			goToPage( self.page - 1 );
		} );
		this.els.pagination.appendChild( prevBtn );

		for ( var i = 1; i <= this.pages; i++ ) {
			var btn = document.createElement( 'button' );
			btn.type = 'button';
			btn.className = 'omni-vc-page-btn' + ( i === this.page ? ' is-active' : '' );
			btn.textContent = String( i );
			if ( i === this.page ) {
				btn.setAttribute( 'aria-current', 'page' );
			}
			( function ( pageNum ) {
				btn.addEventListener( 'click', function () {
					goToPage( pageNum );
				} );
			} )( i );
			this.els.pagination.appendChild( btn );
		}

		nextBtn = document.createElement( 'button' );
		nextBtn.type = 'button';
		nextBtn.className = 'omni-vc-page-btn omni-vc-page-arrow';
		nextBtn.setAttribute( 'aria-label', 'Następna strona' );
		nextBtn.textContent = '›';
		nextBtn.disabled = this.page >= this.pages;
		nextBtn.addEventListener( 'click', function () {
			goToPage( self.page + 1 );
		} );
		this.els.pagination.appendChild( nextBtn );
	};

	// ------------------------------------------------------------------
	// Sloty opakowania.
	// ------------------------------------------------------------------

	OmniVirtualCounter.prototype.isAutoPackageSelectionEnabled = function () {
		return !! ( CFG.settings && CFG.settings.autoPackage );
	};

	OmniVirtualCounter.prototype.isMultiplePackageSelectionEnabled = function () {
		return !! ( CFG.settings && CFG.settings.autoPackage && CFG.settings.multiplePackages );
	};

	OmniVirtualCounter.prototype.findPackageById = function ( packageId ) {
		return this.packages.filter( function ( pkg ) {
			return pkg.id === packageId;
		} )[ 0 ] || null;
	};

	OmniVirtualCounter.prototype.getPackageCandidates = function () {
		var strategy = CFG.settings && CFG.settings.packageStrategy === 'cheapest' ? 'cheapest' : 'smallest';
		var candidates = toArray( this.packages ).filter( function ( pkg ) {
			return !! pkg.active;
		} );

		candidates.sort( function ( left, right ) {
			if ( 'cheapest' === strategy ) {
				return ( parseFloat( left.price ) || 0 ) - ( parseFloat( right.price ) || 0 ) ||
					( parseInt( left.slots, 10 ) || 0 ) - ( parseInt( right.slots, 10 ) || 0 );
			}

			return ( parseInt( left.slots, 10 ) || 0 ) - ( parseInt( right.slots, 10 ) || 0 ) ||
				( parseFloat( left.price ) || 0 ) - ( parseFloat( right.price ) || 0 );
		} );

		return candidates;
	};

	OmniVirtualCounter.prototype.getOccupiedSlotCount = function () {
		return this.slots.filter( function ( slot ) {
			return !! slot;
		} ).length;
	};

	OmniVirtualCounter.prototype.repackBlocksIntoPackage = function ( pkg ) {
		var self = this;
		var blocks = this.getBlocks();
		var originalSlots = this.slots;
		var repacked;
		var fits = true;

		this.slots = new Array( pkg.slots ).fill( null );

		blocks.forEach( function ( block ) {
			var index = self.findFirstFreeBlock( block.span );
			if ( -1 === index ) {
				fits = false;
				return;
			}
			self.occupyBlock( index, block.span, block.payload );
		} );

		repacked = this.slots;
		this.slots = originalSlots;

		return fits ? repacked : null;
	};

	OmniVirtualCounter.prototype.ensurePackageCapacityFor = function ( requiredSlots ) {
		var candidates;
		var selected;
		var repacked;

		if ( ! this.isAutoPackageSelectionEnabled() || ! this.isPackageCounter() ) {
			return false;
		}

		candidates = this.getPackageCandidates();
		for ( var i = 0; i < candidates.length; i++ ) {
			if ( ( parseInt( candidates[ i ].slots, 10 ) || 0 ) < requiredSlots ) {
				continue;
			}

			repacked = this.repackBlocksIntoPackage( candidates[ i ] );
			if ( repacked ) {
				selected = candidates[ i ];
				break;
			}
		}

		if ( ! selected ) {
			if ( this.isMultiplePackageSelectionEnabled() && this.currentPackage ) {
				this.slots = this.slots.concat( new Array( Math.max( 1, parseInt( this.currentPackage.slots, 10 ) || 1 ) ).fill( null ) );
				this.renderSlots();
				return true;
			}

			return false;
		}

		this.slots = repacked;
		this.setPackage( selected, false );
		return true;
	};

	OmniVirtualCounter.prototype.applyServerSelectedPackage = function ( evaluation ) {
		var selected;

		if ( ! evaluation || ! evaluation.package || ! this.currentPackage ) {
			return;
		}

		if ( evaluation.package.id === this.currentPackage.id ) {
			if ( evaluation.slots_total && evaluation.slots_total > this.slots.length ) {
				this.slots = this.slots.concat( new Array( evaluation.slots_total - this.slots.length ).fill( null ) );
			}
			return;
		}

		selected = this.findPackageById( evaluation.package.id );
		if ( ! selected ) {
			selected = evaluation.package;
		}

		if ( evaluation.slots_total && evaluation.slots_total > selected.slots ) {
			this.slots = this.slots.concat( new Array( Math.max( 0, evaluation.slots_total - this.slots.length ) ).fill( null ) );
		} else if ( selected.slots !== this.slots.length ) {
			this.slots = this.repackBlocksIntoPackage( selected ) || this.slots.slice( 0, selected.slots );
		}

		this.setPackage( selected, false );
	};

	OmniVirtualCounter.prototype.setPackage = function ( pkg, resetSlots ) {
		this.currentPackage = pkg;
		this.els.packageSelect.value = String( pkg.id );
		this.els.summaryPackageName.textContent = pkg.name;
		this.ensurePackagePageContains( pkg.id );

		if ( resetSlots ) {
			this.slots = new Array( pkg.slots ).fill( null );
		}

		this.applyPackageSlotsOffset();
		this.renderSlots();
		this.renderPackagePicker();
	};

	/**
	 * To opakowanie moze miec wlasne przesuniecie siatki miejsc (WL:
	 * Opakowania > "Przesuniecie w poziomie/pionie") - nadpisuje wtedy
	 * globalna wartosc z Ustawien (--omni-vc-pkg-slots-offset-x/-y) samym
	 * inline style'em na .omni-vc-package-visual (wygrywa z regula w
	 * arkuszu niezaleznie od !important - patrz konfigurator.css). Gdy
	 * opakowanie nie ma wlasnego przesuniecia (0/brak), usuwamy inline
	 * override, zeby wrocic do globalnej wartosci z Ustawien.
	 */
	OmniVirtualCounter.prototype.applyPackageSlotsOffset = function () {
		if ( ! this.els.packageVisual ) {
			return;
		}

		var pkg = this.currentPackage;
		var offsetX = pkg ? parseInt( pkg.slots_offset_x_px, 10 ) || 0 : 0;
		var offsetY = pkg ? parseInt( pkg.slots_offset_y_px, 10 ) || 0 : 0;

		if ( offsetX ) {
			this.els.packageVisual.style.setProperty( '--omni-vc-pkg-slots-offset-x', offsetX + 'px' );
		} else {
			this.els.packageVisual.style.removeProperty( '--omni-vc-pkg-slots-offset-x' );
		}

		if ( offsetY ) {
			this.els.packageVisual.style.setProperty( '--omni-vc-pkg-slots-offset-y', offsetY + 'px' );
		} else {
			this.els.packageVisual.style.removeProperty( '--omni-vc-pkg-slots-offset-y' );
		}
	};

	OmniVirtualCounter.prototype.ensurePackagePageContains = function ( packageId ) {
		var targetIndex = -1;
		this.packages.some( function ( pkg, index ) {
			if ( pkg.id === packageId ) {
				targetIndex = index;
				return true;
			}
			return false;
		} );

		if ( targetIndex >= 0 ) {
			this.packagePage = Math.floor( targetIndex / this.packagePageSize ) + 1;
		}
	};

	// Liczba kolumn siatki tacy zalezy WYLACZNIE od calkowitej liczby miejsc w
	// opakowaniu (nie od tego, co akurat jest w nich wlozone) - dzieki temu
	// arytmetyka indeksow (wiersz/kolumna) jest stabilna i moze byc uzywana
	// zarowno przez CSS (--omni-vc-slot-cols), jak i przez logike blokow
	// wielosolotowych (fitowanie w wierszu, patrz isRowFit/findFirstFreeBlock).
	OmniVirtualCounter.prototype.getGridCols = function () {
		var total = this.slots.length;
		if ( ! total ) {
			return 6;
		}

		// Admin moze recznie ustawic liczbe kolumn dla tego opakowania (WL:
		// Opakowania > "Liczba kolumn siatki") - np. zeby 7 miejsc ulozylo
		// sie 4+3 zamiast domyslnego 6+1. 0/brak = automatyka jak dotychczas.
		var manualCols = this.currentPackage ? parseInt( this.currentPackage.slots_grid_cols, 10 ) || 0 : 0;
		if ( manualCols > 0 ) {
			return Math.min( 12, Math.max( 1, manualCols ) );
		}

		return Math.min( 12, Math.max( 6, Math.ceil( total / 2 ) ) );
	};

	// Dokowanie w tacy (od 761px, patrz configurator.css): liczba kolumn i rozmiar
	// slotu sa przeliczane tak, zeby caly zestaw miejsc zawsze zmiescil sie w
	// rzeczywistej, sztywnej wysokosci tacy - bez przewijania i bez wystawania
	// poza nia, niezaleznie od wielkosci opakowania (6/12/24 miejsc).
	OmniVirtualCounter.prototype.updateSlotGridSizing = function () {
		var grid = this.els.slotsGrid;
		var total = this.slots.length;

		if ( ! total ) {
			return;
		}

		var cols = this.getGridCols();
		var rows = Math.max( 1, Math.ceil( total / cols ) );
		var gap = 6; // px - musi byc zgodny z "gap" w regule .omni-vc-slots-grid.
		var availW = grid.clientWidth;
		var availH = grid.clientHeight;

		grid.style.setProperty( '--omni-vc-slot-cols', String( cols ) );

		if ( availW > 0 && availH > 0 ) {
			var widthFit = ( availW - ( cols - 1 ) * gap ) / cols;
			var heightFit = ( ( availH - ( rows - 1 ) * gap ) / rows ) * 1.05; // aspect-ratio slotu 1.05/1.
			var size = Math.max( 24, Math.min( widthFit, heightFit ) * 0.97 ); // margines na zaokraglenia.
			grid.style.setProperty( '--omni-vc-slot-size', size + 'px' );
		} else {
			grid.style.removeProperty( '--omni-vc-slot-size' );
		}
	};

	// ------------------------------------------------------------------
	// Bloki wielosolotowe: produkt moze zajmowac N sasiadujacych kratek w
	// JEDNYM wierszu siatki (nigdy nie przechodzi do kolejnego wiersza - patrz
	// isRowFit). Kazda kratka bloku trzyma ta sama dana z polem "anchorIndex"
	// wskazujacym pierwsza (lewa) kratke bloku - to ono identyfikuje blok.
	// ------------------------------------------------------------------

	OmniVirtualCounter.prototype.isRowFit = function ( index, span ) {
		var cols = this.getGridCols();
		return index >= 0 && ( index % cols ) + span <= cols && index + span <= this.slots.length;
	};

	OmniVirtualCounter.prototype.regionFree = function ( index, span ) {
		if ( index < 0 || index + span > this.slots.length ) {
			return false;
		}
		for ( var i = index; i < index + span; i++ ) {
			if ( this.slots[ i ] ) {
				return false;
			}
		}
		return true;
	};

	/**
	 * Zwraca dane bloku, JEŚLI [index, index+span) jest w całości zajęte przez
	 * dokładnie jeden istniejący blok, który zaczyna się w "index" i ma
	 * dokładnie "span" slotów. W przeciwnym razie (częściowe nakładanie, inny
	 * rozmiar bloku) zwraca null - taki drop jest odrzucany.
	 */
	OmniVirtualCounter.prototype.getExactBlockAt = function ( index, span ) {
		var occupant = this.slots[ index ];
		if ( ! occupant || occupant.anchorIndex !== index || ( occupant.slots_required || 1 ) !== span ) {
			return null;
		}
		for ( var i = index; i < index + span; i++ ) {
			var cell = this.slots[ i ];
			if ( ! cell || cell.anchorIndex !== index ) {
				return null;
			}
		}
		return occupant;
	};

	OmniVirtualCounter.prototype.occupyBlock = function ( anchorIndex, span, payload ) {
		for ( var i = 0; i < span; i++ ) {
			this.slots[ anchorIndex + i ] = Object.assign( {}, payload, {
				slots_required: span,
				anchorIndex: anchorIndex,
			} );
		}
	};

	OmniVirtualCounter.prototype.clearBlock = function ( anyIndexInBlock ) {
		var occupant = this.slots[ anyIndexInBlock ];
		if ( ! occupant ) {
			return;
		}
		var anchor = occupant.anchorIndex;
		var span = occupant.slots_required || 1;
		for ( var i = anchor; i < anchor + span && i < this.slots.length; i++ ) {
			if ( this.slots[ i ] && this.slots[ i ].anchorIndex === anchor ) {
				this.slots[ i ] = null;
			}
		}
	};

	OmniVirtualCounter.prototype.findFirstFreeBlock = function ( span ) {
		span = Math.max( 1, span || 1 );
		for ( var i = 0; i <= this.slots.length - span; i++ ) {
			if ( this.isRowFit( i, span ) && this.regionFree( i, span ) ) {
				return i;
			}
		}
		return -1;
	};

	OmniVirtualCounter.prototype.renderSlots = function () {
		var self = this;
		var grid = this.els.slotsGrid;
		grid.innerHTML = '';

		this.updateSlotGridSizing();

		this.slots.forEach( function ( slot, index ) {
			var node = self.els.tmplSlot.content.firstElementChild.cloneNode( true );
			node.dataset.slotIndex = String( index );

			// Kratka "kontynuacji" bloku (rezerwuje miejsce dla produktu zajętego
			// w kotwicy obok, ale sama go nie pokazuje) - żaden obrazek nie jest
			// rozciągany, produkt zawsze wygląda jak normalna, pojedyncza kratka.
			if ( slot && slot.anchorIndex !== index ) {
				node.classList.add( 'is-blocked' );
				node.setAttribute( 'aria-label', ( slot.name || '' ) + ' - ' + ( CFG.i18n.slotBlocked || 'zajęte' ) );
				node.addEventListener( 'click', function () {
					self.removeSlot( index );
				} );
				node.addEventListener( 'keydown', function ( e ) {
					if ( 'Delete' === e.key || 'Backspace' === e.key ) {
						self.removeSlot( index );
					}
				} );
				self.els.slotsGrid.appendChild( node );
				return;
			}

			var span = slot ? ( slot.slots_required || 1 ) : 1;

			if ( slot ) {
				node.classList.add( 'is-occupied' );
				if ( span > 1 ) {
					node.classList.add( 'is-multi' );
					node.dataset.slotsRequired = String( span );
				}
				if ( slot.invalid ) {
					node.classList.add( 'is-invalid' );
				}
				var img = node.querySelector( '.omni-vc-slot-thumb' );
				withImageFallback( img, SLOT_IMG_FALLBACK );
				img.alt = slot.name || '';
				if ( slot.thumbnail ) {
					img.src = slot.thumbnail;
					img.hidden = false;
				} else {
					node.classList.add( 'is-missing-image' );
					img.hidden = true;
				}
				node.querySelector( '.omni-vc-slot-empty-icon' ).hidden = true;
				node.setAttribute( 'aria-label', slot.name || '' );

				var removeBtn = node.querySelector( '.omni-vc-slot-remove' );
				removeBtn.hidden = false;
				removeBtn.addEventListener( 'click', function ( e ) {
					e.stopPropagation();
					self.removeSlot( index );
				} );

				node.addEventListener( 'keydown', function ( e ) {
					if ( 'Delete' === e.key || 'Backspace' === e.key ) {
						self.removeSlot( index );
					}
				} );

				if ( CFG.settings.enableDragDrop ) {
					img.draggable = true;
					self.bindDraggable( img, function () {
						return Object.assign( { fromSlot: index }, slot );
					} );
				}
			} else {
				node.classList.add( 'is-empty' );
			}

			if ( CFG.settings.enableDragDrop ) {
				self.bindDropTarget( node, index );
			}

			self.els.slotsGrid.appendChild( node );
		} );

		this.updateClearAllVisibility();
	};

	OmniVirtualCounter.prototype.addToFirstFreeSlot = function ( payload ) {
		var span = Math.max( 1, parseInt( payload.slots_required, 10 ) || 1 );
		var index = this.findFirstFreeBlock( span );

		if ( -1 === index && this.ensurePackageCapacityFor( this.getOccupiedSlotCount() + span ) ) {
			index = this.findFirstFreeBlock( span );
		}

		if ( -1 === index ) {
			this.pushNotice( 'error', CFG.i18n.full );
			return;
		}
		this.occupyBlock( index, span, payload );
		this.renderSlots();
		this.syncAndValidate();
	};

	OmniVirtualCounter.prototype.addToPickupList = function ( payload ) {
		var existing = this.pickupItems.filter( function ( item ) {
			return item.product_id === payload.product_id && ( item.variation_id || 0 ) === ( payload.variation_id || 0 );
		} )[ 0 ] || null;

		if ( existing ) {
			existing.qty += 1;
		} else {
			this.pickupItems.push( Object.assign( { qty: 1 }, payload ) );
		}

		this.renderSummary();
		this.pushNotice( 'success', 'Dodano do listy odbioru: ' + payload.name );
	};

	OmniVirtualCounter.prototype.removeFromPickupList = function ( index ) {
		if ( index < 0 || index >= this.pickupItems.length ) {
			return;
		}
		this.pickupItems.splice( index, 1 );
		this.renderSummary();
	};

	/**
	 * Ustawia wybrany sposób dostawy dla listy odbioru ("pickup" albo
	 * "shipping") i odświeża wygląd przełącznika w podsumowaniu.
	 *
	 * @param {string} method "pickup" albo "shipping".
	 */
	OmniVirtualCounter.prototype.setPickupDeliveryMethod = function ( method ) {
		this.pickupDeliveryMethod = 'shipping' === method ? 'shipping' : 'pickup';

		if ( ! this.els.deliveryMethod ) {
			return;
		}

		this.els.deliveryMethod.querySelectorAll( '.omni-vc-delivery-method-option' ).forEach( function ( btn ) {
			var active = btn.dataset.method === this.pickupDeliveryMethod;
			btn.classList.toggle( 'is-active', active );
			btn.setAttribute( 'aria-checked', active ? 'true' : 'false' );
		}, this );

		if ( this.els.addToCart ) {
			this.els.addToCart.textContent = 'shipping' === this.pickupDeliveryMethod
				? 'Wyślij do koszyka'
				: 'Wyślij listę do odbioru';
		}
	};

	/**
	 * Wysyła całą listę pieczywa wprost do prawdziwego koszyka WooCommerce
	 * (realna cena, realna dostawa/checkout) - zarówno dla "Wysyłka", jak i
	 * dla "Odbiór osobisty" (klient i tak kończy normalny checkout WC,
	 * wybierając tam odpowiednią metodę dostawy). Serwer sam ponownie
	 * weryfikuje dostępność każdego produktu, a przy wysyłce dodatkowo flagę
	 * Products::META_SHIPPING_ALLOWED - podpowiedź poniżej jest tylko
	 * wygodą dla klienta, nie jedynym zabezpieczeniem.
	 *
	 * @param {string} method "shipping" albo "pickup".
	 */
	OmniVirtualCounter.prototype.submitPickupList = function ( method ) {
		var self = this;

		if ( 'shipping' === method ) {
			var notShippable = this.pickupItems.filter( function ( item ) {
				return ! item.shipping_allowed;
			} );

			if ( notShippable.length ) {
				this.pushNotice(
					'error',
					'Tych produktów nie można wysłać - usuń je z listy albo wybierz odbiór osobisty: ' +
						notShippable.map( function ( item ) { return item.name; } ).join( ', ' )
				);
				return;
			}
		}

		this.els.addToCart.disabled = true;
		this.els.addToCart.classList.add( 'is-loading' );

		apiSend( 'cart/add-products', {
			method: method,
			items: this.pickupItems.map( function ( item ) {
				return {
					product_id: item.product_id,
					variation_id: item.variation_id || 0,
					qty: item.qty || 1,
				};
			} ),
			order_date: this.els.orderDate ? this.els.orderDate.value : '',
		} )
			.catch( function ( err ) {
				console.error( '[Omni Virtual Counter cart/add-products]', err ); // eslint-disable-line no-console
				self.pushNotice( 'error', 'Nie udało się połączyć z koszykiem. Sprawdź połączenie i spróbuj ponownie.' );
				return null;
			} )
			.then( function ( res ) {
				self.els.addToCart.classList.remove( 'is-loading' );
				self.els.addToCart.disabled = ! self.pickupItems.length;

				if ( ! res ) {
					return;
				}

				if ( res.ok && res.data.success ) {
					toArray( res.data.notices ).forEach( function ( n ) {
						self.pushNotice( n.type, n.message );
					} );

					self.triggerWooAddedToCart( res.data );
					document.dispatchEvent( new CustomEvent( 'omni-vc-added-to-cart', { detail: res.data } ) );

					self.pickupItems = [];
					self.renderSummary();
					return;
				}

				var notices = toArray( res.data && res.data.notices );
				if ( ! notices.length && res.data && res.data.message ) {
					notices = [ { type: 'error', message: res.data.message } ];
				}
				if ( ! notices.length ) {
					notices = [ { type: 'error', message: 'Nie udało się dodać produktów do koszyka (HTTP ' + res.status + ').' } ];
				}
				notices.forEach( function ( n ) {
					self.pushNotice( n.type || 'error', n.message );
				} );
			} );
	};

	OmniVirtualCounter.prototype.removeSlot = function ( index ) {
		this.clearBlock( index );
		this.renderSlots();
		this.syncAndValidate();
	};

	/**
	 * Opróżnia CAŁE opakowanie (wszystkie sloty) jednym kliknięciem, zamiast
	 * usuwania produktów pojedynczo przez "×" na każdym slocie.
	 */
	OmniVirtualCounter.prototype.clearAllSlots = function () {
		if ( ! this.slots.some( function ( cell ) { return !! cell; } ) ) {
			return;
		}

		this.slots = new Array( this.slots.length ).fill( null );
		this.renderSlots();
		this.syncAndValidate();
	};

	/**
	 * Odpowiednik clearAllSlots() dla lady pickup_list (lista odbioru) -
	 * czyści całą listę produktów zamiast usuwać je jeden po drugim.
	 */
	OmniVirtualCounter.prototype.clearPickupList = function () {
		if ( ! this.pickupItems.length ) {
			return;
		}

		this.pickupItems = [];
		this.renderSummary();
	};

	OmniVirtualCounter.prototype.triggerWooAddedToCart = function ( data ) {
		if ( ! window.jQuery ) {
			return;
		}

		var $button = this.els.addToCart ? window.jQuery( this.els.addToCart ) : window.jQuery();
		var cartHash = data && data.cart_hash ? data.cart_hash : '';

		window.jQuery( document.body ).trigger( 'wc_fragment_refresh' );
		window.jQuery( document.body ).trigger( 'added_to_cart', [ {}, cartHash, $button ] );
	};

	/**
	 * Obsługa przycisku "Wyczyść wszystko" - dziala na aktualnie widocznej
	 * ladzie (sloty opakowania albo lista odbioru pieczywa), z potwierdzeniem,
	 * zeby przypadkowe klikniecie nie skasowalo calego zestawu klienta.
	 */
	OmniVirtualCounter.prototype.handleClearAll = function () {
		var hasContent = this.isPickupCounter() ? !! this.pickupItems.length : this.slots.some( function ( cell ) { return !! cell; } );

		if ( ! hasContent ) {
			return;
		}

		if ( ! window.confirm( CFG.i18n.confirmClearAll || 'Na pewno wyczyścić cały zestaw?' ) ) {
			return;
		}

		if ( this.isPickupCounter() ) {
			this.clearPickupList();
		} else {
			this.clearAllSlots();
		}
	};

	OmniVirtualCounter.prototype.placeAtSlot = function ( index, payload, fromSlot ) {
		var span = Math.max( 1, parseInt( payload.slots_required, 10 ) || 1 );
		var hasSource = undefined !== fromSlot && null !== fromSlot;
		var deniedMessage = CFG.i18n.doesNotFit || 'Nie można tu umieścić produktu - brak miejsca lub zajęte przez inny produkt.';

		if ( hasSource && fromSlot === index ) {
			return;
		}

		if ( ! this.isRowFit( index, span ) ) {
			this.pushNotice( 'error', deniedMessage );
			return;
		}

		if ( this.regionFree( index, span ) ) {
			if ( hasSource ) {
				this.clearBlock( fromSlot );
			}
			this.occupyBlock( index, span, payload );
		} else {
			var targetOccupant = this.getExactBlockAt( index, span );

			if ( ! targetOccupant ) {
				this.pushNotice( 'error', deniedMessage );
				return;
			}

			if ( hasSource ) {
				var sourceOccupant = this.getExactBlockAt( fromSlot, span );
				if ( ! sourceOccupant ) {
					this.pushNotice( 'error', deniedMessage );
					return;
				}
				// Zamiana miejscami dwóch bloków tego samego rozmiaru.
				this.clearBlock( fromSlot );
				this.clearBlock( index );
				this.occupyBlock( index, span, payload );
				this.occupyBlock( fromSlot, span, targetOccupant );
			} else {
				// Upuszczenie nowego produktu z katalogu na zajęte miejsce - nadpisuje je.
				this.clearBlock( index );
				this.occupyBlock( index, span, payload );
			}
		}

		this.renderSlots();
		this.syncAndValidate();
	};

	// ------------------------------------------------------------------
	// Drag & drop (desktop HTML5 + fallback touch/pointer).
	// ------------------------------------------------------------------

	OmniVirtualCounter.prototype.bindDraggable = function ( el, payloadFn ) {
		var self = this;

		el.addEventListener( 'dragstart', function ( e ) {
			if ( el.closest( '.omni-vc-product.is-unavailable' ) ) {
				e.preventDefault();
				return;
			}
			var payload = payloadFn();
			self.dragData = payload;
			e.dataTransfer.effectAllowed = 'move';
			try {
				e.dataTransfer.setData( 'text/plain', JSON.stringify( payload ) );
			} catch ( err ) { /* Safari fallback korzysta z self.dragData */ }
			el.closest( '.omni-vc-product, .omni-vc-slot' ).classList.add( 'is-dragging' );
		} );

		el.addEventListener( 'dragend', function () {
			var parent = el.closest( '.omni-vc-product, .omni-vc-slot' );
			if ( parent ) {
				parent.classList.remove( 'is-dragging' );
			}
			self.dragData = null;
		} );

		// Fallback dotykowy - natywne dragstart/drop nie działają na dotyku.
		this.bindTouchDrag( el, payloadFn );
	};

	OmniVirtualCounter.prototype.bindDropTarget = function ( node, index ) {
		var self = this;

		node.addEventListener( 'dragover', function ( e ) {
			e.preventDefault();
			e.dataTransfer.dropEffect = 'move';
			node.classList.add( 'is-dragover' );
		} );

		node.addEventListener( 'dragleave', function () {
			node.classList.remove( 'is-dragover' );
		} );

		node.addEventListener( 'drop', function ( e ) {
			e.preventDefault();
			node.classList.remove( 'is-dragover' );

			var payload = self.dragData;
			if ( ! payload ) {
				try {
					payload = JSON.parse( e.dataTransfer.getData( 'text/plain' ) );
				} catch ( err ) {
					return;
				}
			}
			if ( ! payload ) {
				return;
			}

			self.placeAtSlot( index, {
				product_id: payload.product_id,
				variation_id: payload.variation_id || 0,
				name: payload.name,
				price: payload.price,
				thumbnail: payload.thumbnail,
				slots_required: payload.slots_required || 1,
			}, payload.fromSlot );
		} );
	};

	/**
	 * Prosty, ręczny drag & drop dla urządzeń dotykowych (Pointer Events).
	 * Nie zastępuje przycisku "Dodaj" - to tylko rozszerzenie UX.
	 */
	OmniVirtualCounter.prototype.bindTouchDrag = function ( el, payloadFn ) {
		var self = this;
		var ghost = null;
		var startX, startY, dragging = false;

		el.addEventListener(
			'touchstart',
			function ( e ) {
				if ( e.touches.length !== 1 || el.closest( '.omni-vc-product.is-unavailable' ) ) {
					return;
				}
				var touch = e.touches[ 0 ];
				startX = touch.clientX;
				startY = touch.clientY;
				dragging = false;

				var payload = payloadFn();

				function onMove( ev ) {
					var t = ev.touches[ 0 ];
					var dx = t.clientX - startX;
					var dy = t.clientY - startY;

					if ( ! dragging && ( Math.abs( dx ) > 10 || Math.abs( dy ) > 10 ) ) {
						dragging = true;
						ghost = el.cloneNode( true );
						ghost.className = 'omni-vc-drag-ghost';
						ghost.style.position = 'fixed';
						ghost.style.width = '56px';
						ghost.style.height = '56px';
						ghost.style.pointerEvents = 'none';
						ghost.style.zIndex = '99999';
						ghost.style.opacity = '0.85';
						document.body.appendChild( ghost );
					}

					if ( dragging && ghost ) {
						ev.preventDefault();
						ghost.style.left = t.clientX - 28 + 'px';
						ghost.style.top = t.clientY - 28 + 'px';

						self.root.querySelectorAll( '.omni-vc-slot.is-dragover' ).forEach( function ( n ) {
							n.classList.remove( 'is-dragover' );
						} );

						var target = document.elementFromPoint( t.clientX, t.clientY );
						var slotNode = target ? target.closest( '.omni-vc-slot' ) : null;
						if ( slotNode ) {
							slotNode.classList.add( 'is-dragover' );
						}
					}
				}

				function onEnd( ev ) {
					document.removeEventListener( 'touchmove', onMove );
					document.removeEventListener( 'touchend', onEnd );

					if ( ghost ) {
						ghost.remove();
						ghost = null;
					}

					if ( ! dragging ) {
						return;
					}

					var touchEnd = ev.changedTouches[ 0 ];
					var target = document.elementFromPoint( touchEnd.clientX, touchEnd.clientY );
					var slotNode = target ? target.closest( '.omni-vc-slot' ) : null;

					if ( slotNode ) {
						slotNode.classList.remove( 'is-dragover' );
						var index = parseInt( slotNode.dataset.slotIndex, 10 );
						self.placeAtSlot(
							index,
							{
								product_id: payload.product_id,
								variation_id: payload.variation_id || 0,
								name: payload.name,
								price: payload.price,
								thumbnail: payload.thumbnail,
								slots_required: payload.slots_required || 1,
							},
							payload.fromSlot
						);
					}
				}

				document.addEventListener( 'touchmove', onMove, { passive: false } );
				document.addEventListener( 'touchend', onEnd );
			},
			{ passive: true }
		);
	};

	// ------------------------------------------------------------------
	// Walidacja / synchronizacja z serwerem (autorytatywna).
	// ------------------------------------------------------------------

	OmniVirtualCounter.prototype.buildSlotsPayload = function () {
		var payload = [];
		this.slots.forEach( function ( slot, index ) {
			if ( slot ) {
				payload.push( { slot: index, product_id: slot.product_id, variation_id: slot.variation_id || 0 } );
			}
		} );
		return payload;
	};

	OmniVirtualCounter.prototype.syncAndValidate = debounce( function () {
		var self = this;
		var requestSeq = ++this.validationRequestSeq;

		if ( ! this.currentPackage ) {
			return;
		}

		var slots = this.buildSlotsPayload();

		apiSend( 'session', {
			package_id: this.currentPackage.id,
			slots: slots,
			configuration_id: this.configurationId,
			cart_item_key: this.editingCartItemKey,
		} )
			.then( function ( res ) {
				if ( requestSeq !== self.validationRequestSeq ) {
					return;
				}

				if ( ! res.ok ) {
					throw new Error( 'POST /session failed with status ' + res.status );
				}
				self.configurationId = res.data.configuration.configuration_id;
				self.evaluation = res.data.evaluation;
				self.applyServerSelectedPackage( self.evaluation );
				self.hydrateSlotsFromEvaluation( self.evaluation );
				self.renderSlots();
				self.renderSummary();
			} )
			.catch( function ( err ) {
				if ( requestSeq !== self.validationRequestSeq ) {
					return;
				}

				self.reportFatalError( err );
			} );
	}, 400 );

	/**
	 * Pokazuje przycisk "Wyczyść wszystko" tylko wtedy, gdy jest faktycznie
	 * co czyścić (i tylko na ladach package/pickup_list - inquiry_single nie
	 * ma trwałej, wieloelementowej zawartości do czyszczenia zbiorczo).
	 */
	OmniVirtualCounter.prototype.updateClearAllVisibility = function () {
		if ( ! this.els.clearAll ) {
			return;
		}

		var hasContent;
		if ( this.isPickupCounter() ) {
			hasContent = !! this.pickupItems.length;
		} else if ( this.isPackageCounter() ) {
			hasContent = this.slots.some( function ( cell ) { return !! cell; } );
		} else {
			hasContent = false;
		}

		this.els.clearAll.hidden = ! hasContent;
	};

	OmniVirtualCounter.prototype.renderSummary = function () {
		var ev = this.evaluation;

		this.updateClearAllVisibility();

		if ( ! this.isPackageCounter() ) {
			this.renderNonPackageSummary();
			return;
		}

		if ( ! ev || ! ev.package ) {
			this.renderEmptyPackageSummary();
			return;
		}

		this.els.summaryPackageName.textContent = ev.package.name;
		this.els.summarySlots.textContent = ev.slots_used + ' / ' + ev.slots_total;

		var slotsPct = ev.slots_total ? Math.min( 100, ( ev.slots_used / ev.slots_total ) * 100 ) : 0;
		this.els.slotsBar.style.width = slotsPct + '%';
		this.els.slotsBar.classList.toggle( 'is-over', ev.slots_used > ev.slots_total );

		var maxWeight = ev.package.max_weight || 0;
		this.els.summaryWeight.textContent = ev.weight.toFixed( 3 ) + ' / ' + ( maxWeight ? maxWeight + ' kg' : '∞' );
		var weightPct = maxWeight ? Math.min( 100, ( ev.weight / maxWeight ) * 100 ) : 0;
		this.els.weightBar.style.width = weightPct + '%';
		this.els.weightBar.classList.toggle( 'is-over', maxWeight > 0 && ev.weight > maxWeight );

		this.els.summaryProductsValue.textContent = formatPrice( ev.products_value, this.currency );
		this.els.summaryPackagePrice.textContent = formatPrice( ev.package_price, this.currency );
		this.els.summaryTotal.textContent = formatPrice( ev.total, this.currency );

		this.els.summaryItems.innerHTML = '';
		toArray( ev.items ).forEach( function ( item ) {
			var li = document.createElement( 'li' );
			li.className = 'omni-vc-summary-item';
			li.textContent = item.qty + ' × ' + item.name;
			this.els.summaryItems.appendChild( li );
		}, this );

		this.els.emptyHint.hidden = ev.slots_used > 0;
		this.els.addToCart.disabled = ! ev.valid || ev.slots_used === 0;

		this.showEvaluationNotices( ev );
	};

	/**
	 * Stan lady package, zanim serwer zdąży zwrócić wycenę (świeże
	 * przełączenie na tę ladę, zanim cokolwiek trafi do slotu) - bez tego
	 * panel podsumowania zostawałby z treścią poprzednio oglądanej lady
	 * (patrz renderSummary() powyżej), bo nic by go nie nadpisało.
	 */
	OmniVirtualCounter.prototype.renderEmptyPackageSummary = function () {
		var pkg = this.currentPackage;

		if ( this.els.summaryPackageName ) {
			this.els.summaryPackageName.textContent = pkg ? pkg.name : '';
		}
		if ( this.els.summarySlots ) {
			this.els.summarySlots.textContent = '0 / ' + ( pkg ? pkg.slots : 0 );
		}
		if ( this.els.slotsBar ) {
			this.els.slotsBar.style.width = '0%';
			this.els.slotsBar.classList.toggle( 'is-over', false );
		}
		if ( this.els.summaryWeight ) {
			var maxWeight = pkg ? pkg.max_weight || 0 : 0;
			this.els.summaryWeight.textContent = '0.000 / ' + ( maxWeight ? maxWeight + ' kg' : '∞' );
		}
		if ( this.els.weightBar ) {
			this.els.weightBar.style.width = '0%';
			this.els.weightBar.classList.toggle( 'is-over', false );
		}
		if ( this.els.summaryProductsValue ) {
			this.els.summaryProductsValue.textContent = formatPrice( 0, this.currency );
		}
		if ( this.els.summaryPackagePrice ) {
			this.els.summaryPackagePrice.textContent = pkg ? formatPrice( pkg.price, this.currency ) : '–';
		}
		if ( this.els.summaryTotal ) {
			this.els.summaryTotal.textContent = formatPrice( pkg ? pkg.price : 0, this.currency );
		}
		if ( this.els.summaryItems ) {
			this.els.summaryItems.innerHTML = '';
		}
		if ( this.els.emptyHint ) {
			this.els.emptyHint.hidden = false;
		}
		if ( this.els.addToCart ) {
			this.els.addToCart.disabled = true;
		}
	};

	OmniVirtualCounter.prototype.renderNonPackageSummary = function () {
		var self = this;
		var total = this.pickupItems.reduce( function ( sum, item ) {
			return sum + ( parseFloat( item.price ) || 0 ) * ( parseInt( item.qty, 10 ) || 1 );
		}, 0 );

		if ( this.isInquiryCounter() ) {
			if ( this.els.summaryPackageName ) {
				this.els.summaryPackageName.textContent = this.inquiryProduct ? this.inquiryProduct.name : 'Zamówienie tortu';
			}
			if ( ! this.inquiryProduct && this.els.summaryItems ) {
				this.els.summaryItems.innerHTML = '<li class="omni-vc-summary-item">Wybierz tort na ladzie i kliknij CTA pod produktem.</li>';
			}
			// Lada tortów nie liczy Miejsc/Wagi/Opakowania jak pozostałe lady
			// (klient dopytuje o wycenę zamiast samemu dopełniać zestaw) -
			// zeruj je, żeby po przełączeniu z innej lady nie zostawała
			// przypadkowa treść (np. "Odbiór w cukierni" z listy pieczywa).
			if ( this.els.summarySlots ) {
				this.els.summarySlots.textContent = '–';
			}
			if ( this.els.slotsBar ) {
				this.els.slotsBar.style.width = '0%';
				this.els.slotsBar.classList.toggle( 'is-over', false );
			}
			if ( this.els.summaryWeight ) {
				this.els.summaryWeight.textContent = '–';
			}
			if ( this.els.weightBar ) {
				this.els.weightBar.style.width = '0%';
				this.els.weightBar.classList.toggle( 'is-over', false );
			}
			if ( this.els.summaryProductsValue ) {
				this.els.summaryProductsValue.textContent = '–';
			}
			if ( this.els.summaryPackagePrice ) {
				this.els.summaryPackagePrice.textContent = '–';
			}
			if ( this.els.summaryTotal ) {
				this.els.summaryTotal.textContent = '–';
			}
			if ( this.els.emptyHint ) {
				this.els.emptyHint.hidden = true;
			}
			if ( this.els.addToCart ) {
				this.els.addToCart.disabled = true;
			}
			this.updateInquiryFormVisibility();
			return;
		}

		if ( this.els.summaryPackageName ) {
			this.els.summaryPackageName.textContent = 'Lista do odbioru w cukierni';
		}
		if ( this.els.summarySlots ) {
			this.els.summarySlots.textContent = String( this.pickupItems.reduce( function ( sum, item ) {
				return sum + ( parseInt( item.qty, 10 ) || 1 );
			}, 0 ) );
		}
		var deliveryLabel = 'shipping' === this.pickupDeliveryMethod ? 'Wysyłka' : 'Odbiór w cukierni';

		if ( this.els.summaryWeight ) {
			this.els.summaryWeight.textContent = deliveryLabel;
		}
		if ( this.els.slotsBar ) {
			this.els.slotsBar.style.width = this.pickupItems.length ? '100%' : '0%';
		}
		if ( this.els.weightBar ) {
			this.els.weightBar.style.width = '0%';
		}
		if ( this.els.summaryProductsValue ) {
			this.els.summaryProductsValue.textContent = formatPrice( total, this.currency );
		}
		if ( this.els.summaryPackagePrice ) {
			this.els.summaryPackagePrice.textContent = deliveryLabel;
		}
		if ( this.els.summaryTotal ) {
			this.els.summaryTotal.textContent = formatPrice( total, this.currency );
		}

		this.els.summaryItems.innerHTML = '';
		this.pickupItems.forEach( function ( item, index ) {
			var li = document.createElement( 'li' );
			var remove = document.createElement( 'button' );
			li.className = 'omni-vc-summary-item omni-vc-pickup-item';
			li.textContent = item.qty + ' × ' + item.name;
			remove.type = 'button';
			remove.className = 'omni-vc-pickup-remove';
			remove.textContent = 'Usuń';
			remove.addEventListener( 'click', function () {
				self.removeFromPickupList( index );
			} );
			li.appendChild( remove );
			self.els.summaryItems.appendChild( li );
		} );

		if ( ! this.pickupItems.length ) {
			this.els.summaryItems.innerHTML = '<li class="omni-vc-summary-item">Lista jest pusta.</li>';
		}

		if ( this.els.emptyHint ) {
			this.els.emptyHint.hidden = this.pickupItems.length > 0;
		}
		if ( this.els.addToCart ) {
			this.els.addToCart.disabled = ! this.pickupItems.length;
		}
	};

	/**
	 * Pokazuje/chowa formularz zapytania o tort w miejscu, gdzie na
	 * pozostałych ladach jest podliczenie zestawu - widoczny tylko, gdy
	 * klient faktycznie wybrał konkretny tort (kliknął CTA pod produktem).
	 */
	OmniVirtualCounter.prototype.updateInquiryFormVisibility = function () {
		var active = this.isInquiryCounter() && !! this.inquiryProduct;

		if ( this.els.summary ) {
			this.els.summary.classList.toggle( 'is-inquiry-form-active', active );
		}
		if ( this.els.inquiryForm ) {
			this.els.inquiryForm.hidden = ! active;
		}
	};

	/**
	 * Otwiera formularz zapytania dla wybranego tortu (zamiast dawnego
	 * natychmiastowego mailto:) - klient wypełnia dane, a wysyłką po
	 * kliknięciu "Wyślij zapytanie" zajmuje się submitInquiryForm().
	 *
	 * @param {Object} payload Dane produktu (patrz resolveDragPayload()).
	 */
	OmniVirtualCounter.prototype.showInquiryForm = function ( payload ) {
		this.inquiryProduct = payload;

		if ( this.els.inquiryDate ) {
			this.els.inquiryDate.min = this.getMinDateString( CFG.settings.leadTimeCakeDays );
		}

		this.renderSummary();

		if ( this.els.inquiryForm && 'function' === typeof this.els.inquiryForm.scrollIntoView ) {
			this.els.inquiryForm.scrollIntoView( { behavior: 'smooth', block: 'nearest' } );
		}

		if ( this.els.inquiryName ) {
			this.els.inquiryName.focus();
		}
	};

	/**
	 * Waliduje i wysyła formularz zapytania o tort przez serwer (wp_mail(),
	 * patrz Includes\Inquiry) - nie przez mailto:, żeby działało nawet bez
	 * skonfigurowanego programu pocztowego u klienta.
	 */
	OmniVirtualCounter.prototype.submitInquiryForm = function () {
		var self = this;

		if ( ! this.inquiryProduct ) {
			this.pushNotice( 'warning', 'Wybierz tort na ladzie i kliknij CTA pod produktem.' );
			return;
		}

		if ( this.els.inquiryForm.reportValidity && ! this.els.inquiryForm.reportValidity() ) {
			return;
		}

		var payload = {
			counter: this.currentCounter ? this.currentCounter.slug : '',
			product_id: this.inquiryProduct.product_id,
			name: this.els.inquiryName ? this.els.inquiryName.value.trim() : '',
			phone: this.els.inquiryPhone ? this.els.inquiryPhone.value.trim() : '',
			email: this.els.inquiryEmail ? this.els.inquiryEmail.value.trim() : '',
			qty: parseInt( this.els.inquiryQty ? this.els.inquiryQty.value : '1', 10 ) || 1,
			pickup_date: this.els.inquiryDate ? this.els.inquiryDate.value : '',
			pickup_location: this.els.inquiryLocation ? this.els.inquiryLocation.value.trim() : '',
			wants_invoice: !! ( this.els.inquiryInvoice && this.els.inquiryInvoice.checked ),
			nip: this.els.inquiryNip ? this.els.inquiryNip.value.trim() : '',
			message: this.els.inquiryMessage ? this.els.inquiryMessage.value.trim() : '',
		};

		this.els.inquirySubmit.disabled = true;
		this.els.inquirySubmit.classList.add( 'is-loading' );

		apiSend( 'inquiry', payload )
			.catch( function ( err ) {
				console.error( '[Omni Virtual Counter inquiry]', err ); // eslint-disable-line no-console
				self.pushNotice( 'error', 'Nie udało się wysłać zapytania. Sprawdź połączenie i spróbuj ponownie.' );
				return null;
			} )
			.then( function ( res ) {
				self.els.inquirySubmit.disabled = false;
				self.els.inquirySubmit.classList.remove( 'is-loading' );

				if ( ! res ) {
					return;
				}

				if ( res.ok && res.data.success ) {
					toArray( res.data.notices ).forEach( function ( n ) {
						self.pushNotice( n.type, n.message );
					} );
					self.els.inquiryForm.reset();
					self.inquiryProduct = null;
					self.renderSummary();
					return;
				}

				var notices = toArray( res.data && res.data.notices );
				if ( ! notices.length ) {
					notices = [ { type: 'error', message: 'Nie udało się wysłać zapytania (HTTP ' + res.status + ').' } ];
				}
				notices.forEach( function ( n ) {
					self.pushNotice( n.type || 'error', n.message );
				} );
			} );
	};

	/**
	 * Kody komunikatów, które dotyczą pojedynczego produktu/pozycji i mogą
	 * wystąpić wielokrotnie dla różnych produktów w tym samym zestawie -
	 * zamiast zalewać klienta osobnym banerem na każdy produkt, łączymy je
	 * w jeden zbiorczy komunikat (czytelniej i bardziej elegancko).
	 */
	var AGGREGATE_LABELS = {
		product_unavailable: 'produktów w zestawie jest niedostępnych',
		product_limit: 'produktów w zestawie przekracza dozwoloną ilość',
		category_disallowed: 'produktów nie jest dozwolonych w wybranym opakowaniu',
		category_not_allowed: 'produktów nie należy do kategorii dozwolonych w tym opakowaniu',
		product_not_shippable: 'produktów w zestawie zwykle nie jest wysyłanych kurierem',
	};

	OmniVirtualCounter.prototype.showEvaluationNotices = function ( ev ) {
		var self = this;
		var notices = toArray( ev.notices );
		var groups = {};
		var singles = [];

		notices.forEach( function ( notice ) {
			if ( AGGREGATE_LABELS[ notice.code ] ) {
				groups[ notice.code ] = groups[ notice.code ] || [];
				groups[ notice.code ].push( notice );
			} else {
				singles.push( notice );
			}
		} );

		var newKeys = [];

		singles.forEach( function ( notice ) {
			newKeys.push( self.pushNotice( notice.type, notice.message ) );
		} );

		Object.keys( groups ).forEach( function ( code ) {
			var group = groups[ code ];
			var message = 1 === group.length
				? group[ 0 ].message
				: group.length + ' ' + AGGREGATE_LABELS[ code ] + '.';
			newKeys.push( self.pushNotice( group[ 0 ].type, message ) );
		} );

		// Usuń komunikaty z POPRZEDNIEJ walidacji, które przestały być aktualne
		// (np. licznik "5 produktów…" zastąpiony przez "6 produktów…") - bez
		// tego stare, nieaktualne banery zostawałyby zawieszone na ekranie.
		toArray( this.evaluationNoticeKeys ).forEach( function ( key ) {
			if ( -1 === newKeys.indexOf( key ) ) {
				self.dismissNotice( key );
			}
		} );

		this.evaluationNoticeKeys = newKeys;
	};

	// ------------------------------------------------------------------
	// Komunikaty (aria-live).
	// ------------------------------------------------------------------

	OmniVirtualCounter.prototype.pushNotice = function ( type, message ) {
		if ( ! message ) {
			return null;
		}

		var key = type + ':' + message;
		var self = this;

		// Ten sam komunikat już widoczny - tylko odśwież czas wygaśnięcia,
		// zamiast dublować identyczny baner na ekranie.
		if ( this.activeNotices[ key ] ) {
			clearTimeout( this.activeNotices[ key ].timer );
			this.activeNotices[ key ].timer = setTimeout( function () {
				self.dismissNotice( key );
			}, 'error' === type ? 9000 : 5000 );
			return key;
		}

		var node = this.els.tmplNotice.content.firstElementChild.cloneNode( true );
		node.dataset.type = type;
		node.classList.add( 'omni-vc-notice-' + type );
		var textEl = node.querySelector( '.omni-vc-notice-text' );
		textEl.textContent = message;
		textEl.title = message;

		node.querySelector( '.omni-vc-notice-dismiss' ).addEventListener( 'click', function () {
			self.dismissNotice( key );
		} );

		this.els.notices.appendChild( node );

		var timer = setTimeout(
			function () {
				self.dismissNotice( key );
			},
			'error' === type ? 9000 : 5000
		);

		this.activeNotices[ key ] = { node: node, timer: timer };

		return key;
	};

	OmniVirtualCounter.prototype.dismissNotice = function ( key ) {
		var entry = this.activeNotices[ key ];
		if ( ! entry ) {
			return;
		}
		clearTimeout( entry.timer );
		if ( entry.node.parentNode ) {
			entry.node.remove();
		}
		delete this.activeNotices[ key ];
	};

	// ------------------------------------------------------------------
	// Zdarzenia statyczne (raz, przy starcie).
	// ------------------------------------------------------------------

	OmniVirtualCounter.prototype.bindStaticEvents = function () {
		var self = this;

		if ( this.els.slotsGrid ) {
			window.addEventListener(
				'resize',
				debounce( function () {
					self.updateSlotGridSizing();
				}, 150 )
			);
		}

		if ( this.els.search ) {
			this.els.search.addEventListener(
				'input',
				debounce( function () {
					self.searchTerm = self.els.search.value.trim();
					self.page = 1;
					self.loadProducts();
				}, 350 )
			);
		}

		this.els.packageSelect.addEventListener( 'change', function () {
			self.handlePackageChange( parseInt( self.els.packageSelect.value, 10 ) );
		} );

		if ( this.els.packagePrev ) {
			this.els.packagePrev.addEventListener( 'click', function () {
				self.packagePage = Math.max( 1, self.packagePage - 1 );
				self.renderPackagePicker();
			} );
		}

		if ( this.els.packageNext ) {
			this.els.packageNext.addEventListener( 'click', function () {
				var totalPages = Math.max( 1, Math.ceil( self.packages.length / self.packagePageSize ) );
				self.packagePage = Math.min( totalPages, self.packagePage + 1 );
				self.renderPackagePicker();
			} );
		}

		if ( this.els.resumeContinue && this.els.resumeBar ) {
			this.els.resumeContinue.addEventListener( 'click', function () {
				self.els.resumeBar.hidden = true;
				if ( self.canResumeConfiguration( self.pendingResume ) ) {
					self.applyConfiguration( self.pendingResume.configuration, self.pendingResume.evaluation );
				} else {
					apiSend( 'session', {}, 'DELETE' );
					self.pendingResume = null;
					self.startFresh();
				}
			} );
		}

		if ( this.els.resumeClear && this.els.resumeBar ) {
			this.els.resumeClear.addEventListener( 'click', function () {
				self.els.resumeBar.hidden = true;
				apiSend( 'session', {}, 'DELETE' ).then( function () {
					self.startFresh();
				} );
			} );
		}

		// Bind in capture phase as well: some mobile themes attach delegated
		// handlers to buttons/links and stop bubbling before our storefront code
		// sees the event. A normal click still covers keyboard accessibility.
		this.els.addToCart.addEventListener( 'click', function ( event ) {
			event.preventDefault();
			event.stopPropagation();
			self.submitAddToCart();
		}, true );

		if ( this.els.clearAll ) {
			this.els.clearAll.addEventListener( 'click', function ( event ) {
				event.preventDefault();
				self.handleClearAll();
			} );
		}

		if ( this.els.deliveryMethod ) {
			this.els.deliveryMethod.addEventListener( 'click', function ( event ) {
				var btn = event.target.closest( '.omni-vc-delivery-method-option' );
				if ( ! btn ) {
					return;
				}
				self.setPickupDeliveryMethod( btn.dataset.method );
			} );
		}

		if ( this.els.inquiryForm ) {
			this.els.inquiryForm.addEventListener( 'submit', function ( event ) {
				event.preventDefault();
				self.submitInquiryForm();
			} );
		}

		if ( this.els.inquiryInvoice && this.els.inquiryNipWrap ) {
			this.els.inquiryInvoice.addEventListener( 'change', function () {
				var checked = self.els.inquiryInvoice.checked;
				self.els.inquiryNipWrap.hidden = ! checked;
				if ( self.els.inquiryNip ) {
					self.els.inquiryNip.required = checked;
				}
			} );
		}

		if ( this.els.inquiryBack ) {
			this.els.inquiryBack.addEventListener( 'click', function () {
				self.closeInquiryDetail();
			} );
		}
	};

	/**
	 * Zwraca listę aktualnie umieszczonych bloków (jeden wpis na kotwicę), w
	 * kolejności ich indeksu - używane przy przepakowywaniu tacy po zmianie
	 * opakowania (patrz handlePackageChange).
	 */
	OmniVirtualCounter.prototype.getBlocks = function () {
		var blocks = [];
		for ( var i = 0; i < this.slots.length; i++ ) {
			var cell = this.slots[ i ];
			if ( cell && cell.anchorIndex === i ) {
				blocks.push( { payload: cell, span: cell.slots_required || 1 } );
			}
		}
		return blocks;
	};

	OmniVirtualCounter.prototype.handlePackageChange = function ( packageId ) {
		var newPkg = this.packages.filter( function ( p ) {
			return p.id === packageId;
		} )[ 0 ];

		if ( ! newPkg ) {
			return;
		}

		// Przepakuj wszystkie istniejące bloki (w dotychczasowej kolejności) do
		// tacy o nowym rozmiarze - działa identycznie przy powiększaniu i
		// zmniejszaniu opakowania. Liczymy to "na sucho" na tymczasowej tablicy
		// (findFirstFreeBlock/occupyBlock operują na this.slots, więc na chwilę
		// podmieniamy kontekst), żeby móc zapytać o potwierdzenie PRZED
		// odrzuceniem produktów, które się nie zmieszczą.
		var self = this;
		var blocks = this.getBlocks();
		var originalSlots = this.slots;
		var dropped = [];

		this.slots = new Array( newPkg.slots ).fill( null );

		blocks.forEach( function ( block ) {
			var index = self.findFirstFreeBlock( block.span );
			if ( -1 === index ) {
				dropped.push( block );
			} else {
				self.occupyBlock( index, block.span, block.payload );
			}
		} );

		var repacked = this.slots;
		this.slots = originalSlots;

		if ( dropped.length ) {
			var msg =
				'Nowe opakowanie (' + newPkg.slots + ' miejsc) jest za małe, żeby pomieścić cały obecny zestaw. ' +
				dropped.length + ' z ' + blocks.length + ' produktów trzeba będzie usunąć z zestawu. Kontynuować?';

			if ( ! window.confirm( msg ) ) { // eslint-disable-line no-alert
				this.els.packageSelect.value = String( this.currentPackage.id );
				return;
			}
		}

		this.slots = repacked;
		this.currentPackage = newPkg;
		this.els.summaryPackageName.textContent = newPkg.name;
		this.ensurePackagePageContains( newPkg.id );
		this.renderSlots();
		this.renderPackagePicker();
		this.loadProducts();
		this.syncAndValidate();
	};

	OmniVirtualCounter.prototype.submitAddToCart = function () {
		var self = this;

		if ( this.isInquiryCounter() ) {
			this.pushNotice( 'warning', 'Wybierz tort na ladzie i kliknij "Ustal szczegóły zamówienia".' );
			return;
		}

		if ( ! this.validateOrderDate() ) {
			return;
		}

		if ( this.isPickupCounter() ) {
			if ( ! this.pickupItems.length ) {
				this.pushNotice( 'warning', 'Najpierw dodaj pieczywo do listy odbioru.' );
				return;
			}
			this.submitPickupList( this.pickupDeliveryMethod );
			return;
		}

		if ( this.addToCartPending ) {
			return;
		}

		if ( ! this.evaluation || ! this.evaluation.valid || this.evaluation.slots_used === 0 ) {
			this.pushNotice( 'warning', 'Najpierw dodaj produkty do opakowania i popraw ewentualne błędy zestawu.' );
			return;
		}

		this.addToCartPending = true;
		this.els.addToCart.disabled = true;
		this.els.addToCart.classList.add( 'is-loading' );

		apiSend( 'cart/add', {
			package_id: this.currentPackage.id,
			slots: this.buildSlotsPayload(),
			configuration_id: this.configurationId,
			cart_item_key: this.editingCartItemKey,
			order_date: this.els.orderDate ? this.els.orderDate.value : '',
		} )
			.catch( function ( err ) {
				console.error( '[Omni Virtual Counter cart/add]', err ); // eslint-disable-line no-console
				self.pushNotice( 'error', 'Nie udało się połączyć z koszykiem. Sprawdź połączenie i spróbuj ponownie.' );
				self.addToCartPending = false;
				self.els.addToCart.classList.remove( 'is-loading' );
				self.els.addToCart.disabled = false;
				return null;
			} )
			.then( function ( res ) {
				if ( ! res ) {
					return;
				}

				if ( res.ok && res.data.success ) {
					toArray( res.data.notices ).forEach( function ( n ) {
						self.pushNotice( n.type, n.message );
					} );

					self.addToCartPending = false;
					self.els.addToCart.classList.remove( 'is-loading' );

					// Refresh WooCommerce mini-cart/header counters after our custom REST
					// add-to-cart. Without this, many mobile themes keep showing "0"
					// even though the server cart already contains the set.
					self.triggerWooAddedToCart( res.data );
					document.dispatchEvent( new CustomEvent( 'omni-vc-added-to-cart', { detail: res.data } ) );

					self.editingCartItemKey = null;
					self.configurationId = null;

					if ( res.data.cart_url ) {
						var link = document.createElement( 'a' );
						link.href = res.data.cart_url;
						link.className = 'omni-vc-btn omni-vc-btn-ghost omni-vc-goto-cart';
						link.textContent = 'Przejdź do koszyka';
						self.els.addToCart.insertAdjacentElement( 'afterend', link );
					}

					self.startFresh();
				} else {
					var notices = toArray( res.data && res.data.notices );
					if ( ! notices.length && res.data && res.data.message ) {
						notices = [ { type: 'error', message: res.data.message } ];
					}
					if ( ! notices.length ) {
						notices = [ { type: 'error', message: 'Nie udało się dodać zestawu do koszyka (HTTP ' + res.status + ').' } ];
					}
					notices.forEach( function ( n ) {
						self.pushNotice( n.type || 'error', n.message );
					} );
					self.addToCartPending = false;
					self.els.addToCart.classList.remove( 'is-loading' );
					self.els.addToCart.disabled = false;
				}
			} );
	};

	// --------------------------------------------------------------
	// Bootstrap.
	// --------------------------------------------------------------

	function boot() {
		var apps = document.querySelectorAll( '.omni-vc-app' );
		apps.forEach( function ( app ) {
			new OmniVirtualCounter( app ); // eslint-disable-line no-new
		} );
	}

	if ( 'loading' === document.readyState ) {
		document.addEventListener( 'DOMContentLoaded', boot );
	} else {
		boot();
	}
} )();
